<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { reactive, ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVisitasStore } from 'dashboard/stores/visitas';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTracking } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useEscClose } from 'dashboard/composables/useEscClose';
import ImovelAPI from 'dashboard/api/imoveis';
import PropertyOffersAPI from 'dashboard/api/propertyOffers';
import { buildRealEstateCapabilityContext } from 'dashboard/helper/permissionsHelper';
import { REAL_ESTATE_SURFACES } from 'dashboard/constants/permissions';
import {
  getVisitaCardStatus,
  VISITA_CARD_STATUS,
  VISITA_CARD_STATUS_CLASSES,
  VISITA_CARD_STATUS_LABELS,
  getVisitaStatusClass,
  getVisitaStatusLabel,
  isVisitaCheckInDone,
} from 'dashboard/helper/lais/visitaPresenter';

import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import LaisFilterButton from 'dashboard/components-next/LaisFilterButton.vue';
import LaisFilterBar from 'dashboard/components-next/LaisFilterBar.vue';
import LaisActionBar from 'dashboard/components-next/LaisActionBar.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisLastUpdate from 'dashboard/components-next/LaisLastUpdate.vue';
import LaisMetricStrip from 'dashboard/components-next/LaisMetricStrip.vue';
import VisitasOperationalPanel from './VisitasOperationalPanel.vue';
import VisitsListState from '../components/VisitsListState.vue';
import VisitNowAnchor from '../components/VisitNowAnchor.vue';
import UnreadVisitMessageSnackbar from '../components/UnreadVisitMessageSnackbar.vue';
import VisitStatusCard from '../components/VisitStatusCard.vue';
import AgentActivityAlert from '../components/AgentActivityAlert.vue';
import QualifiedVisitorAlert from '../components/QualifiedVisitorAlert.vue';
import KeyHolderInfoDialog from '../components/KeyHolderInfoDialog.vue';
import VisitAgendaInfoModal from '../components/VisitAgendaInfoModal.vue';
import VisitAvailabilityModal from '../components/VisitAvailabilityModal.vue';
import VisitBlockedSheetModal from '../components/VisitBlockedSheetModal.vue';
import VisitConfirmationModal from '../components/VisitConfirmationModal.vue';
import VisitCreateVisitModal from '../components/VisitCreateVisitModal.vue';
import VisitFinishChoiceModal from '../components/VisitFinishChoiceModal.vue';
import VisitQuickActionsDrawer from '../components/VisitQuickActionsDrawer.vue';
import VisitWantCancelModal from '../components/VisitWantCancelModal.vue';
import VisitsAgendaTabs from '../components/VisitsAgendaTabs.vue';
import VisitsAgendaTimeline from '../components/VisitsAgendaTimeline.vue';
import VisitsBucketTabs from '../components/VisitsBucketTabs.vue';
import VisitsOperationalTable from '../components/VisitsOperationalTable.vue';
import VisitsOrderByDropdown from '../components/VisitsOrderByDropdown.vue';
import ScheduledClientCard from '../components/ScheduledClientCard.vue';
import DoneVisitRichCard from '../components/DoneVisitRichCard.vue';
import StalledVisitCard from '../components/StalledVisitCard.vue';
import PendingFinishVisitCard from '../components/PendingFinishVisitCard.vue';
import VisitSectionTitle from '../components/VisitSectionTitle.vue';
import {
  AGENDA_QUICK_ACTIONS,
  actionCompleted,
  agentSupportId,
  buildConfirmVisitMessage,
  buildIntroductionMessage,
  buildOfferMessage,
  buildVisitSuggestHouseMessage,
  canSendIntroduction,
  canSendOffer,
  canSuggestHouse,
  canTalkToOwner,
  contactName,
  contactPhone,
  isAgentSupportInProgress,
  propertyAddress,
  propertyId,
  quickActionAttributes,
} from '../utils/agendaQuickActions';
import {
  channelErrorMessage,
  channelTrackingPayload,
  openResolvedChannel,
  resolveChannel,
  resolveOwnerChannel,
} from 'dashboard/helper/lais/channelRouter';
import { trackQuintoEvent } from 'dashboard/helper/lais/trackingEvents';
import { isAgentAllowedForHouse } from 'dashboard/helper/lais/agentEligibility';
import {
  VISIT_TRACKING_EVENTS,
  visitFinalizationUserAction,
} from '../utils/visitTracking';
import { summarizeBatchVisitConfirmation } from '../utils/batchVisitConfirmation';
import {
  VISIT_CANCELLATION_NOTICE,
  VISIT_CANCEL_CONFIRM_LABEL,
  VISIT_CANCEL_CONFIRM_TITLE,
  VISIT_CANCEL_DETAILS_TITLE,
  VISIT_CANCEL_INVALID_DATE_MESSAGE,
  VISIT_CANCEL_INVALID_FIRST_DAY_MESSAGE,
  VISIT_CANCEL_REASON_TITLE,
  VISIT_CANCEL_RESCHEDULE_LINK,
  VISIT_CANCEL_RESCHEDULE_TEXT,
  VISIT_CANCEL_RESPONSIBLE_OPTIONS,
  VISIT_CANCEL_SUSPEND_BODY,
  VISIT_CANCEL_SUSPEND_CONFIRM_LABEL,
  VISIT_CANCEL_SUSPEND_FLOW_BODY,
  VISIT_CANCEL_SUSPEND_FLOW_TITLE,
  VISIT_CANCEL_SUSPEND_HELPER,
  VISIT_CANCEL_SUSPEND_LABEL,
  VISIT_CANCEL_SUSPEND_LAST_DAY_LABEL,
  VISIT_CANCEL_SUSPEND_PERIOD_LABEL,
  VISIT_CANCEL_SUSPEND_SUCCESS_MESSAGE,
  VISIT_CANCEL_SUSPEND_SUCCESS_TITLE,
  VISIT_CANCEL_SUSPEND_TITLE,
  VISIT_CANCEL_SUCCESS_MESSAGE,
  VISIT_CANCEL_SUCCESS_TITLE,
  VISIT_CANCEL_EXPIRED_MESSAGE,
  VISIT_CONFIRM_EXPIRED_MESSAGE,
  VISIT_HIGH_DEMAND_LABEL,
  VISIT_TIME_EXPIRED_LABEL,
  VISITS_EMPTY_STATE_DESCRIPTION,
  VISITS_EMPTY_STATE_TITLE,
  VISITS_ERROR_STATE_DESCRIPTION,
  VISITS_ERROR_STATE_TITLE,
  cancelReasonLabel,
  cancelResponsibleLabel,
  entryAccessLabel,
  getCancelMessage,
  getCancelReasonOptions,
  republishAlertMessage,
  visitIntentionLabel,
} from '../utils/visitFlowCopy';
import {
  buildScheduledClientCards,
  SCHEDULED_VISITS_ORDER_BY_QUERY,
  SCHEDULED_VISITS_TEXT,
  SCHEDULED_VISITS_TRACKING_EVENTS,
} from '../utils/visitScheduledPresenter';
import { AGENDA_SATELLITE_TRACKING_EVENTS } from '../utils/agendaSatellitePresenter';
import {
  runtimeBadgeForVisit,
  runtimeToneClass,
} from '../utils/visitOperationalRuntime';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const store = useVisitasStore();
const { track } = useTracking();
const { isAdmin } = useAdmin();

const VISIT_AUTOMATION_EVENTS = [
  { key: 'visit_requested' },
  { key: 'visit_confirmed' },
  { key: 'visit_rescheduled' },
  { key: 'visit_canceled' },
  { key: 'visit_completed' },
  { key: 'visit_no_show' },
  { key: 'visit_stalled' },
  { key: 'visit_unsuccessful' },
];
const showAutomationMenu = ref(false);
const visitAutomationEventNames = new Set(
  VISIT_AUTOMATION_EVENTS.map(({ key }) => key)
);
const automationMenuItems = computed(() => [
  {
    action: 'create_automation',
    value: 'visit_requested',
    label: t('AUTOMATION.EVENTS.VISIT_REQUESTED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'visit_confirmed',
    label: t('AUTOMATION.EVENTS.VISIT_CONFIRMED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'visit_rescheduled',
    label: t('AUTOMATION.EVENTS.VISIT_RESCHEDULED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'visit_canceled',
    label: t('AUTOMATION.EVENTS.VISIT_CANCELED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'visit_completed',
    label: t('AUTOMATION.EVENTS.VISIT_COMPLETED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'visit_no_show',
    label: t('AUTOMATION.EVENTS.VISIT_NO_SHOW'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'visit_stalled',
    label: t('AUTOMATION.EVENTS.VISIT_STALLED'),
    icon: 'i-lucide-workflow',
  },
  {
    action: 'create_automation',
    value: 'visit_unsuccessful',
    label: t('AUTOMATION.EVENTS.VISIT_UNSUCCESSFUL'),
    icon: 'i-lucide-workflow',
  },
]);

const currentAccount = computed(() => {
  const accounts = currentUser.value?.accounts || [];
  return accounts.find(account => account.id === accountId.value) || {};
});

const routeValue = value => (Array.isArray(value) ? value[0] : value);
const routeString = (key, fallback = '') =>
  String(routeValue(route.query[key]) || fallback);
const routeQueryString = value => String(routeValue(value) || '').trim();
const routePositiveNumber = (key, fallback) => {
  const parsed = Number(routeValue(route.query[key]));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const routeNonNegativeNumber = (key, fallback) => {
  const parsed = Number(routeValue(route.query[key]));
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
};
const AGENDA_DAYS_TO_DISPLAY = 8;
const VISIT_BUCKET_ORDER = [
  'upcoming',
  'pending',
  'to_finish',
  'past',
  'canceled',
];
const VISIT_BUCKET_MATRIX = Object.freeze({
  upcoming: {
    value: 'upcoming',
    label: 'Próximas',
    aliases: ['proximas', 'próximas', 'upcoming', 'agendadas', 'scheduled'],
    helper:
      'Visitas futuras solicitadas, agendadas ou confirmadas para acompanhar sem perder prazo.',
    defaultSort: 'visitTime,asc',
    empty: {
      title: VISITS_EMPTY_STATE_TITLE,
      description: VISITS_EMPTY_STATE_DESCRIPTION,
    },
    loading: {
      title: 'Carregando próximas visitas',
      description: 'Buscando as próximas visitas da agenda.',
    },
    error: {
      title: VISITS_ERROR_STATE_TITLE,
      description: VISITS_ERROR_STATE_DESCRIPTION,
    },
    fixture: 'visitas-index-bucket-agendadas',
    screenshot: 'visitas-index-bucket-agendadas-loading-empty-error',
  },
  pending: {
    value: 'pending',
    label: 'Pendentes',
    aliases: ['pendentes', 'pending'],
    helper: 'Visitas que ainda precisam de confirmação ou revisão operacional.',
    defaultSort: 'visitTime,asc',
    empty: {
      title: 'Sem visitas pendentes',
      description:
        'Quando houver visitas aguardando confirmação, elas aparecerão aqui.',
    },
    loading: {
      title: 'Carregando visitas pendentes',
      description: 'Buscando confirmações e revisões operacionais pendentes.',
    },
    error: {
      title: 'Não foi possível carregar visitas pendentes',
      description: VISITS_ERROR_STATE_DESCRIPTION,
    },
    fixture: 'visitas-index-bucket-pendentes',
    screenshot: 'visitas-index-bucket-pendentes-loading-empty-error',
  },
  to_finish: {
    value: 'to_finish',
    label: 'A finalizar',
    aliases: ['a_finalizar', 'finalizar', 'to_finish', 'visitas_a_finalizar'],
    helper:
      'Visitas iniciadas ou com finalização pendente que ainda precisam de conclusão.',
    defaultSort: 'visitTime,asc',
    empty: {
      title: 'Tudo pronto por aqui, você não tem visitas a finalizar',
      description:
        'Quando uma visita precisar de finalização, ela aparecerá aqui.',
    },
    loading: {
      title: 'Carregando visitas a finalizar',
      description: 'Buscando visitas que ainda precisam de conclusão.',
    },
    error: {
      title: 'Não foi possível carregar visitas a finalizar',
      description: VISITS_ERROR_STATE_DESCRIPTION,
    },
    fixture: 'visitas-index-bucket-a-finalizar',
    screenshot: 'visitas-index-bucket-a-finalizar-loading-empty-error',
  },
  past: {
    value: 'past',
    label: 'Passadas',
    aliases: ['passadas', 'past'],
    helper:
      'Visitas realizadas, expiradas, sem sucesso ou marcadas como não compareceu.',
    defaultSort: 'visitTime,desc',
    empty: {
      title: 'Sem visitas passadas',
      description:
        'As visitas realizadas ou expiradas aparecerão aqui para consulta.',
    },
    loading: {
      title: 'Carregando visitas passadas',
      description: 'Buscando visitas realizadas, expiradas e sem sucesso.',
    },
    error: {
      title: 'Não foi possível carregar visitas passadas',
      description: VISITS_ERROR_STATE_DESCRIPTION,
    },
    fixture: 'visitas-index-bucket-passadas',
    screenshot: 'visitas-index-bucket-passadas-loading-empty-error',
  },
  canceled: {
    value: 'canceled',
    label: 'Canceladas',
    aliases: ['canceladas', 'canceled'],
    helper:
      'Visitas canceladas, com motivo registrado e próximos passos do anúncio.',
    defaultSort: 'visitTime,desc',
    empty: {
      title: 'Sem visitas canceladas',
      description:
        'As visitas canceladas aparecerão aqui com o motivo registrado.',
    },
    loading: {
      title: 'Carregando visitas canceladas',
      description: 'Buscando cancelamentos e motivos registrados.',
    },
    error: {
      title: 'Não foi possível carregar visitas canceladas',
      description: VISITS_ERROR_STATE_DESCRIPTION,
    },
    fixture: 'visitas-index-bucket-canceladas',
    screenshot: 'visitas-index-bucket-canceladas-loading-empty-error',
  },
});
const normalizedVisitBuckets = Object.freeze(
  Object.values(VISIT_BUCKET_MATRIX).reduce((acc, bucket) => {
    [bucket.value, ...bucket.aliases].forEach(alias => {
      acc[String(alias).toLowerCase()] = bucket.value;
    });
    return acc;
  }, {})
);
const allowedVisitBucket = value =>
  normalizedVisitBuckets[String(value || '').toLowerCase()] || 'upcoming';
const VISIT_BUCKET_ROUTE_SEGMENTS = Object.freeze({
  upcoming: [
    '/visitas',
    '/visits',
    '/visit',
    '/visitas/proximas',
    '/visitas/clientes-agendados',
    '/visitas/clientes-com-visita-agendada',
    '/visitas-agendadas',
  ],
  pending: ['/visitas/pendentes'],
  to_finish: ['/visitas/a-finalizar'],
  past: ['/visitas/passadas'],
  canceled: ['/visitas/canceladas'],
});
const pathMatchesVisitBucket = (path, segment) =>
  path.endsWith(segment) || path.includes(`${segment}?`);
const isVisitBucketListPath = path =>
  Object.values(VISIT_BUCKET_ROUTE_SEGMENTS)
    .flat()
    .some(segment => pathMatchesVisitBucket(path, segment));
const visitBucketFromPath = () => {
  const path = route.path || '';

  if (
    VISIT_BUCKET_ROUTE_SEGMENTS.pending.some(segment =>
      pathMatchesVisitBucket(path, segment)
    )
  ) {
    return 'pending';
  }
  if (
    VISIT_BUCKET_ROUTE_SEGMENTS.to_finish.some(segment =>
      pathMatchesVisitBucket(path, segment)
    )
  ) {
    return 'to_finish';
  }
  if (
    VISIT_BUCKET_ROUTE_SEGMENTS.past.some(segment =>
      pathMatchesVisitBucket(path, segment)
    )
  ) {
    return 'past';
  }
  if (
    VISIT_BUCKET_ROUTE_SEGMENTS.canceled.some(segment =>
      pathMatchesVisitBucket(path, segment)
    )
  ) {
    return 'canceled';
  }
  return 'upcoming';
};
const visitRuntimeBadge = visita => runtimeBadgeForVisit(visita);
const operationalViewFromPath = () => {
  const path = route.path || '';
  if (
    path.includes('/agenda/disponibilidade') ||
    path.includes('/agenda/bloqueios')
  ) {
    return 'blocks';
  }
  if (path.includes('/chaves') || path.includes('/keys')) return 'keys';
  if (path.includes('/agenda')) return 'agenda';
  if (isVisitBucketListPath(path)) return 'list';
  return 'agenda';
};
const OPERATIONAL_PANEL_SECTION_ALIASES = new Set([
  'agenda',
  'chaves',
  'keys',
  'alertas',
  'alerts',
  'acompanhamento',
  'recuperacao',
  'recuperação',
  'operacao',
  'operação',
  'operation',
  'recovery',
]);
const compactQuery = query =>
  Object.fromEntries(
    Object.entries(query).filter(([, value]) => value !== '' && value != null)
  );

const VISIT_CONTEXT_QUERY_KEYS = Object.freeze([
  'business_context',
  'businessContext',
  'origin',
  'origem',
  'source',
  'from',
  'click_origin',
  'supply_origin',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'offer_id',
  'proposal_id',
  'imovel_id',
]);
const VISIT_ID_FILTER_KEYS = Object.freeze([
  'contact_ids',
  'imovel_ids',
  'user_id',
]);
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const isIsoDate = value =>
  typeof value === 'string' &&
  ISO_DATE_PATTERN.test(value) &&
  !Number.isNaN(Date.parse(value));
const sanitizeIdList = value => {
  if (Array.isArray(value)) {
    return value
      .map(item => String(item || '').trim())
      .filter(Boolean)
      .join(',');
  }
  return String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
    .join(',');
};
const contextQueryFromRoute = () =>
  VISIT_CONTEXT_QUERY_KEYS.reduce((acc, key) => {
    const value = routeString(key);
    if (value) acc[key] = value;
    return acc;
  }, {});
const idFiltersFromRoute = () =>
  VISIT_ID_FILTER_KEYS.reduce((acc, key) => {
    const value = sanitizeIdList(route.query[key]);
    if (value) acc[key] = value;
    return acc;
  }, {});

const isLoading = ref(false);
const loadError = ref(false);
const capabilityError = ref(null);
const lastUpdatedAt = ref(null);
const page = ref(routePositiveNumber('page', 1));
const perPage = ref(routePositiveNumber('per_page', 10));
const selected = ref(new Set());
const isConfirmingSelected = ref(false);
const showFilters = ref(false);
const visitBucket = ref(
  allowedVisitBucket(
    routeString('tab') || routeString('visit_bucket') || visitBucketFromPath()
  )
);
const defaultSortForBucket = bucket =>
  VISIT_BUCKET_MATRIX[bucket]?.defaultSort || 'visitTime,asc';
const visitBuckets = VISIT_BUCKET_ORDER.map(
  bucket => VISIT_BUCKET_MATRIX[bucket]
);
const currentVisitBucket = computed(
  () =>
    visitBuckets.find(bucket => bucket.value === visitBucket.value) ||
    visitBuckets[0]
);
const allowedSortOrder = value =>
  [
    'visitTime,desc',
    'visitTime,asc',
    'scheduled_at,desc',
    'scheduled_at,asc',
  ].includes(String(value || ''))
    ? String(value)
    : defaultSortForBucket(visitBucket.value);
const sortOrder = ref(
  allowedSortOrder(
    routeString('sorted_by') ||
      routeString('sortedBy') ||
      routeString('order_by') ||
      routeString('orderBy') ||
      defaultSortForBucket(visitBucket.value)
  )
);
const visitType = ref(routeString('visit_type', 'all'));
const statusFilter = ref(routeString('status', 'todos'));
const contactQuery = ref(routeString('contact_query'));
const imovelQuery = ref(routeString('imovel_query'));
const userQuery = ref(routeString('user_query'));
const businessContext = ref(
  routeString('business_context') || routeString('businessContext')
);
const contactIds = ref(sanitizeIdList(route.query.contact_ids));
const imovelIds = ref(sanitizeIdList(route.query.imovel_ids));
const userId = ref(routeString('user_id'));
const exactDate = ref(routeString('date'));
const startDate = ref(routeString('start_date'));
const endDate = ref(routeString('end_date'));
const showCancelModal = ref(false);
const showAvailabilityModal = ref(false);
const showCreateVisitModal = ref(false);
const showFinishVisitModal = ref(false);
const showQuickActionsDrawer = ref(false);
const showWantToCancelModal = ref(false);
const showAgendaInfoModal = ref(false);
const showKeyHolderInfoDialog = ref(false);
const showVisitConfirmationModal = ref(false);
const showVisitConfirmationSuccess = ref(false);
const visitConfirmationLoading = ref(false);
const showBlockedSheet = ref(false);
const blockedVisit = ref(null);
const blockedNotifyLoading = ref(false);
const blockedNotifyDone = ref(false);
const blockedNotifyError = ref('');
const showAnticipationUnavailable = ref(false);
const anticipationUnavailableReason = ref('');
const finishVisitCandidate = ref(null);
const confirmationVisit = ref(null);
const quickActionsVisit = ref(null);
const agendaInfoKind = ref('responsible');
const agendaActiveIndex = ref(0);
const scheduledClientsOrder = ref(
  SCHEDULED_VISITS_ORDER_BY_QUERY.CLOSER_VISITS
);
const cancelForm = reactive({
  responsible: 'demand',
  reason: 'client_stopped_responding',
  notes: '',
  suspendListing: false,
  suspensionStartsAt: '',
  suspensionEndsAt: '',
});
const availabilityVisit = ref(null);
const availabilityDays = ref([]);
const selectedAvailabilityDay = ref(0);
const isLoadingAvailability = ref(false);
const isSavingAvailability = ref(false);
const availabilityError = ref('');
let isSyncingRouteState = false;
let selectVisitBucket;
const visitas = computed(() => store.getVisitasList || []);
const meta = computed(() => store.getMeta || {});
const capabilitiesContext = computed(() =>
  buildRealEstateCapabilityContext({
    surface: REAL_ESTATE_SURFACES.VISITAS_INDEX,
    account: currentAccount.value,
    meta: meta.value,
    error: capabilityError.value,
  })
);
const capabilityFor = action =>
  capabilitiesContext.value.actions[action] || {
    visible: false,
    disabled: true,
    allowed: false,
  };
const canViewVisits = computed(() => capabilityFor('view').visible);
const canUseVisitFilters = computed(() => capabilityFor('filters').visible);
const canCreateVisit = computed(() => capabilityFor('create').visible);
const canCreateVisitAction = computed(() => capabilityFor('create').allowed);
const canConfirmVisits = computed(() => capabilityFor('confirm').allowed);
const canCancelVisits = computed(() => capabilityFor('cancel').allowed);
const canFinishVisits = computed(() => capabilityFor('finish').allowed);
const canReviewVisits = computed(() => capabilityFor('review').visible);
const canReviewVisitsAction = computed(() => capabilityFor('review').allowed);
const canRescheduleVisits = computed(() => capabilityFor('reschedule').allowed);
const canManageAvailability = computed(
  () => capabilityFor('availability').allowed
);
const canUseQuickVisitMessages = computed(
  () => capabilityFor('quick_message').allowed
);
const canSendVisitOffer = computed(() => capabilityFor('offer').allowed);
const visitsCapabilityNotice = computed(() => {
  const { state } = capabilitiesContext.value;

  if (state.permissionDenied) {
    return {
      tone: 'danger',
      title: 'Acesso restrito',
      description:
        'Seu perfil não possui a capability canônica para visualizar ou operar visitas nesta conta.',
    };
  }
  if (state.stale) {
    return {
      tone: 'warning',
      title: 'Dados desatualizados',
      description:
        'A agenda foi marcada como stale; leitura segue disponível, mas confirmação, cancelamento e reagendamento ficam bloqueados.',
    };
  }
  if (state.partial) {
    return {
      tone: 'warning',
      title: 'Resultado parcial',
      description:
        'A agenda retornou dados parciais. Ações operacionais ficam bloqueadas até uma atualização completa.',
    };
  }

  return null;
});
const totalCount = computed(
  () => meta.value.count || meta.value.totalCount || visitas.value.length
);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(totalCount.value / perPage.value))
);
const selectedVisits = computed(() =>
  visitas.value.filter(visita => selected.value.has(visita.id))
);
const selectedVisit = computed(() =>
  selectedVisits.value.length === 1 ? selectedVisits.value[0] : null
);
const activeQuickVisit = computed(() => quickActionsVisit.value || {});
const visitSummary = visita => visita?.summary || {};
const visitCancellationSummary = visita =>
  visitSummary(visita).cancellation || {};
const visitAdditionalAttributes = visita =>
  visita?.additionalAttributes || visita?.additional_attributes || {};
const imovelAdditionalAttributes = visita =>
  visita?.imovel?.additionalAttributes ||
  visita?.imovel?.additional_attributes ||
  {};
const normalizedBoolean = value =>
  value === true || value === 'true' || value === 1 || value === '1';
const normalizedToken = value =>
  String(value || '')
    .trim()
    .toLowerCase();
const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');
const visitSources = visita => [
  visita || {},
  visitSummary(visita),
  visitAdditionalAttributes(visita),
  visita?.imovel || {},
  imovelAdditionalAttributes(visita),
  visita?.contact || {},
];
const visitValue = (visita, ...keys) =>
  visitSources(visita)
    .flatMap(source => keys.map(key => source?.[key]))
    .find(value => value !== undefined && value !== null);
const visitBooleanFlag = (visita, ...keys) =>
  keys.some(key => normalizedBoolean(visitValue(visita, key)));
const visitDateValue = visita => visita?.scheduledAt || visita?.scheduled_at;
const visitTime = visita => {
  const date = new Date(visitDateValue(visita) || 0);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};
const visitImageSrc = visita =>
  firstPresent(
    visita?.imovel?.coverUrl,
    visita?.imovel?.cover_url,
    visita?.imovel?.thumbnailUrl,
    visita?.imovel?.thumbnail_url,
    visita?.imovel?.imageUrl,
    visita?.imovel?.image_url,
    visita?.imovel?.media?.[0]?.url,
    visita?.imovel?.midias?.[0]?.url
  ) || '';
const finishVisitImageSrc = computed(() =>
  visitImageSrc(finishVisitCandidate.value)
);
const keyHolderLink = computed(
  () =>
    firstPresent(
      activeQuickVisit.value?.imovel?.publicUrl,
      activeQuickVisit.value?.imovel?.public_url,
      activeQuickVisit.value?.imovel?.linkExterno,
      activeQuickVisit.value?.imovel?.link_externo,
      activeQuickVisit.value?.imovel?.url
    ) || ''
);
const isPrimaryMarketVisit = visita => {
  const source = normalizedToken(
    firstPresent(
      visitValue(visita, 'sourceLayer', 'source_layer'),
      visitValue(visita, 'catalogSource', 'catalog_source'),
      visitValue(visita, 'listingSource', 'listing_source')
    )
  );
  return (
    visitBooleanFlag(visita, 'primaryMarket', 'primary_market') ||
    ['primary_market', 'mercado_primario', 'orulo', 'new_development'].includes(
      source
    )
  );
};
const isTenantLivingVisit = visita => {
  const occupancy = normalizedToken(
    firstPresent(
      visitValue(visita, 'tenantLiving', 'tenant_living'),
      visitValue(visita, 'houseOccupancy', 'house_occupancy'),
      visitValue(visita, 'occupancy')
    )
  );
  return (
    visitBooleanFlag(visita, 'tenantLiving', 'tenant_living') ||
    ['tenant_living', 'tenant', 'occupied', 'morador'].includes(occupancy)
  );
};
const isThirdPartyVisit = visita => {
  const source = normalizedToken(
    firstPresent(
      visitValue(visita, 'listingSource', 'listing_source'),
      visitValue(visita, 'sourceLayer', 'source_layer')
    )
  );
  return (
    visitBooleanFlag(visita, 'thirdParty', 'third_party', 'isThirdParty') ||
    ['third_party', 'terceiro', 'external'].includes(source)
  );
};
const isAgentSupportVisit = visita =>
  visitBooleanFlag(
    visita,
    'agentSupport',
    'agent_support',
    'requiresAgentSupport',
    'requires_agent_support'
  );
const isQualifiedVisitor = visita =>
  visitBooleanFlag(
    visita,
    'qualifiedVisitor',
    'qualified_visitor',
    'isQualified',
    'is_qualified'
  );
const isPoolVisit = visita => {
  const attrs = visitAdditionalAttributes(visita);
  const summary = visitSummary(visita);
  return (
    visitBooleanFlag(
      visita,
      'isPool',
      'is_pool',
      'poolVisit',
      'pool_visit',
      'visitPool',
      'visit_pool'
    ) ||
    [attrs.visitors, attrs.visitVisitors, summary.visitors].some(
      visitors => Array.isArray(visitors) && visitors.length > 1
    )
  );
};
const decoratedVisit = visita => ({
  ...visita,
  primaryMarket: isPrimaryMarketVisit(visita),
  tenantLiving: isTenantLivingVisit(visita),
  thirdParty: isThirdPartyVisit(visita),
  agentSupport: isAgentSupportVisit(visita),
  isPool: isPoolVisit(visita),
});
const visitCardView = visita => ({
  ...decoratedVisit(visita),
  imovel: {
    ...(visita?.imovel || {}),
    coverUrl: visitImageSrc(visita),
    thumbnailUrl: visitImageSrc(visita),
  },
});
const SAFE_TRACKING_KEYS = new Set([
  'account_id',
  'action',
  'action_origin',
  'block_reason',
  'bucket_fixture',
  'bucket_screenshot',
  'card_status',
  'channel',
  'context',
  'date',
  'day',
  'domain',
  'from_bucket',
  'has_end_date',
  'has_exact_date',
  'has_start_date',
  'house_id',
  'imovel_id',
  'opened',
  'order_by',
  'offer_link_already_sent',
  'page',
  'per_page',
  'reason',
  'responsible',
  'selected_count',
  'sort_order',
  'sorted_by',
  'source',
  'status',
  'suspend_listing',
  'time',
  'trigger',
  'usedInternalConversation',
  'visit_bucket',
  'visit_id',
  'visit_status',
  'visit_type',
  'visits_count',
  'warning',
]);
const SAFE_TRACKING_PREFIXES = ['has_', 'is_', 'can_'];
const SAFE_TRACKING_SUFFIXES = ['_id', '_count', '_length'];
const SAFE_QUERY_TRACKING_KEYS = new Set([
  'has_contact_query',
  'has_imovel_query',
  'has_user_query',
  'contact_query_length',
  'imovel_query_length',
  'user_query_length',
]);
const PII_TRACKING_KEY_PATTERN =
  /(address|bairro|cidade|email|endereco|link|mensagem|message|name|nome|notes|observacao|payload|phone|query|rua|street|telefone|text|token|url|whatsapp)/i;
const PII_TRACKING_VALUE_PATTERN =
  /(https?:\/\/|wa\.me\/|api\.whatsapp\.com|[^\s@]+@[^\s@]+\.[^\s@]+|^\+?\d{8,}$)/i;
const isSafeTrackingKey = key =>
  SAFE_TRACKING_KEYS.has(key) ||
  SAFE_QUERY_TRACKING_KEYS.has(key) ||
  SAFE_TRACKING_PREFIXES.some(prefix => key.startsWith(prefix)) ||
  SAFE_TRACKING_SUFFIXES.some(suffix => key.endsWith(suffix));
const isSafeTrackingValue = value => {
  if (value == null) return true;
  if (typeof value === 'boolean' || typeof value === 'number') return true;
  if (typeof value !== 'string') return false;

  return !PII_TRACKING_VALUE_PATTERN.test(value.trim());
};
const safeTrackingProperties = properties =>
  Object.entries(properties || {}).reduce((safeProperties, [key, value]) => {
    if (!isSafeTrackingKey(key) && PII_TRACKING_KEY_PATTERN.test(key)) {
      return safeProperties;
    }
    if (Array.isArray(value)) {
      const safeValues = value.filter(isSafeTrackingValue);
      if (safeValues.length) safeProperties[key] = safeValues;
      return safeProperties;
    }
    if (!isSafeTrackingValue(value)) return safeProperties;

    safeProperties[key] = value;
    return safeProperties;
  }, {});
const trackVisitFlowEvent = (event, properties = {}) => {
  try {
    track(event, safeTrackingProperties(properties));
  } catch {
    // Tracking must never block operational visit flows.
  }
};
const trackReferenceEvent = (event, properties = {}) => {
  try {
    trackQuintoEvent(track, event, {
      domain: 'visitas',
      visit_bucket: visitBucket.value,
      ...safeTrackingProperties(properties),
    });
  } catch {
    // Reference parity tracking must not block operational visit flows.
  }
};
const queryTrackingSummary = value => {
  const normalized = String(value || '').trim();
  return {
    present: Boolean(normalized),
    length: normalized.length,
  };
};
const scheduledVisitSearchTrackingPayload = () => {
  const contact = queryTrackingSummary(contactQuery.value);
  const imovel = queryTrackingSummary(imovelQuery.value);
  const user = queryTrackingSummary(userQuery.value);

  return {
    has_contact_query: contact.present,
    has_imovel_query: imovel.present,
    has_user_query: user.present,
    contact_query_length: contact.length,
    imovel_query_length: imovel.length,
    user_query_length: user.length,
  };
};
const visitIndexFilterTrackingPayload = (trigger = 'filters') => ({
  trigger,
  visit_type: visitType.value,
  status: statusFilter.value,
  sort_order: sortOrder.value,
  has_exact_date: Boolean(exactDate.value),
  has_start_date: Boolean(startDate.value),
  has_end_date: Boolean(endDate.value),
  page: page.value,
  ...scheduledVisitSearchTrackingPayload(),
});
const trackVisitIndexFilterApplied = trigger => {
  trackReferenceEvent('finished_schedulings_filter_applied', {
    ...visitIndexFilterTrackingPayload(trigger),
    bucket_fixture: currentVisitBucket.value.fixture,
    bucket_screenshot: currentVisitBucket.value.screenshot,
  });
};
const visitFlowTrackingPayload = (visita, extras = {}) => ({
  visit_id: visita?.id,
  imovel_id: propertyId(visita) || visita?.imovel?.id,
  visit_status: normalizedToken(
    firstPresent(
      visita?.status_v3,
      visita?.statusV3,
      visita?.status,
      visitValue(visita, 'flowStatus')
    )
  ),
  ...extras,
});
const blockedReasonLabels = {
  suspended: 'Imóvel suspenso temporariamente para visitas.',
  unavailable: 'Imóvel indisponível ou fora da carteira ativa.',
  no_slots: 'Sem horários publicados para receber visita.',
  permission_denied: 'A conta não possui permissão para agendar esta visita.',
  listing_unpublished: 'Anúncio despublicado no momento.',
  listing_suspended: 'Anúncio suspenso no momento.',
};
const rawBlockedReason = visita => {
  const summary = visitSummary(visita);
  const attrs = visitAdditionalAttributes(visita);
  const imovelAttrs = imovelAdditionalAttributes(visita);
  return (
    summary.blockedReason ||
    summary.blocked_reason ||
    summary.blockReason ||
    attrs.visiting_blocked_reason ||
    attrs.visitingBlockedReason ||
    attrs.blocked_reason ||
    attrs.blockedReason ||
    imovelAttrs.visiting_blocked_reason ||
    imovelAttrs.visitingBlockedReason ||
    ''
  );
};
const isVisitBlocked = visita => {
  const summary = visitSummary(visita);
  const attrs = visitAdditionalAttributes(visita);
  const imovel = visita?.imovel || {};
  const imovelAttrs = imovelAdditionalAttributes(visita);
  const listingStatus = String(
    imovel.listingStatus || imovel.listing_status || ''
  ).toLowerCase();
  const status = String(imovel.status || '').toLowerCase();

  return (
    normalizedBoolean(summary.blocked) ||
    normalizedBoolean(summary.visitingBlocked) ||
    normalizedBoolean(summary.visiting_blocked) ||
    normalizedBoolean(attrs.visiting_blocked) ||
    normalizedBoolean(attrs.visitingBlocked) ||
    normalizedBoolean(imovelAttrs.visits_blocked) ||
    normalizedBoolean(imovelAttrs.visitsBlocked) ||
    Boolean(imovel.suspendedUntil || imovel.suspended_until) ||
    ['suspended', 'suspenso'].includes(listingStatus) ||
    ['inativo', 'vendido', 'alugado', 'reservado'].includes(status)
  );
};
const visitingBlockedReason = visita => {
  const summary = visitSummary(visita);
  const reason =
    summary.blockedReasonLabel ||
    summary.blocked_reason_label ||
    summary.blockReasonLabel ||
    rawBlockedReason(visita);
  const normalized = String(reason || '').toLowerCase();

  if (blockedReasonLabels[normalized]) return blockedReasonLabels[normalized];
  if (reason) return reason;
  if (visita?.imovel?.suspendedUntil || visita?.imovel?.suspended_until) {
    return blockedReasonLabels.suspended;
  }
  return 'Não há horário disponível para esta visita agora.';
};
const visitTableLabels = computed(
  () =>
    visitas.value.find(visita => visita.summary)?.summary?.tableLabels || {
      address: 'Imóvel',
      status: 'Status',
      context: 'Anúncio',
      authorization: 'Entrada no imóvel',
    }
);
const operationalTableCopy = {
  visitType: 'Tipo de visita',
  client: 'Cliente',
  date: 'Data',
  checkInConfirmed: 'Check-in confirmado',
  resolveBlockedVisit: 'Resolver visita bloqueada',
  finishVisit: 'Finalizar visita',
  reviewVisit: 'Revisar visita',
  itemsPerPage: 'Itens por página',
  previousPage: 'Página anterior',
  nextPage: 'Próxima página',
  pageLabel: (pageNumber, totalPageCount) =>
    `Página ${pageNumber} de ${totalPageCount}`,
};
const isVisitExpired = visita => {
  if (!visita?.scheduledAt) return false;
  if (
    !['solicitada', 'agendada', 'confirmada', 'paralisada'].includes(
      visita.status
    )
  ) {
    return false;
  }

  return new Date(visita.scheduledAt).getTime() < Date.now();
};
const selectedHasExpiredVisit = computed(() =>
  selectedVisits.value.some(visita => isVisitExpired(visita))
);
const hasActiveFilters = computed(
  () =>
    showFilters.value ||
    visitType.value !== 'all' ||
    (statusFilter.value && statusFilter.value !== 'todos') ||
    !!contactQuery.value ||
    !!imovelQuery.value ||
    !!userQuery.value ||
    !!exactDate.value ||
    !!startDate.value ||
    !!endDate.value
);

const visitTypeFilters = [
  { value: 'all', label: 'Todas' },
  { value: 'my_supply', label: 'Meus imóveis' },
  { value: 'my_clients', label: 'Meus clientes' },
  { value: 'lead_gen', label: 'Clientes Imudei' },
];

const operationalViews = [
  {
    value: 'agenda',
    label: 'Agenda',
    icon: 'i-lucide-calendar-days',
    helper: '',
  },
  {
    value: 'list',
    label: 'Visitas',
    icon: 'i-lucide-list-checks',
    helper: 'Acompanhe, filtre e revise as visitas do funil operacional.',
  },
  {
    value: 'blocks',
    label: 'Bloqueios',
    icon: 'i-lucide-calendar-off',
    helper:
      'Cadastre indisponibilidades temporárias ou recorrentes por corretor.',
  },
  {
    value: 'keys',
    label: 'Chaves',
    icon: 'i-lucide-key-round',
    helper: 'Controle retirada, posse e devolução de chaves dos imóveis.',
  },
];
const allowedOperationalView = value =>
  operationalViews.some(view => view.value === value) ? value : 'agenda';

const operationalView = ref(
  allowedOperationalView(routeString('view') || operationalViewFromPath())
);
const activeOperationalView = computed(
  () =>
    operationalViews.find(view => view.value === operationalView.value) ||
    operationalViews[0]
);
const isOperationalList = computed(() => operationalView.value === 'list');
const isAgendaView = computed(() => operationalView.value === 'agenda');
const headerTitle = computed(() => {
  if (isOperationalList.value) return 'Visitas';
  if (operationalView.value === 'blocks') return 'Bloqueios de agenda';
  if (operationalView.value === 'keys') return 'Chaves';
  return 'Agenda';
});
const hasOperationalPanelSection = computed(() =>
  OPERATIONAL_PANEL_SECTION_ALIASES.has(
    routeString('section').trim().toLowerCase()
  )
);

const headerHelper = computed(() => {
  if (isAgendaView.value) return '';
  if (isOperationalList.value) return currentVisitBucket.value.helper;
  return activeOperationalView.value.helper;
});

const visitBucketTabId = bucket => `visitas-bucket-tab-${bucket}`;
const visitBucketPanelId = bucket => `visitas-bucket-panel-${bucket}`;
const focusVisitBucketTab = bucket => {
  if (typeof document === 'undefined') return;
  const requestFrame = (window.requestAnimationFrame || window.setTimeout).bind(
    window
  );
  requestFrame(() => {
    document.getElementById(visitBucketTabId(bucket))?.focus();
  });
};
const selectVisitBucketByIndex = index => {
  const nextBucket = visitBuckets[index]?.value;
  if (!nextBucket) return;

  selectVisitBucket(nextBucket);
  focusVisitBucketTab(nextBucket);
};
const handleVisitBucketKeydown = (event, index) => {
  const keyActions = {
    ArrowLeft: () =>
      selectVisitBucketByIndex(
        (index - 1 + visitBuckets.length) % visitBuckets.length
      ),
    ArrowRight: () =>
      selectVisitBucketByIndex((index + 1) % visitBuckets.length),
    Home: () => selectVisitBucketByIndex(0),
    End: () => selectVisitBucketByIndex(visitBuckets.length - 1),
  };
  const action = keyActions[event.key];
  if (!action) return;

  event.preventDefault();
  action();
};

const localDateKey = value => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const buildAgendaDates = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Array.from({ length: AGENDA_DAYS_TO_DISPLAY }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() + index);
    return localDateKey(date);
  });
};

const allowedAgendaIndex = value => {
  const parsed = Number(value);
  return Number.isInteger(parsed) &&
    parsed >= 0 &&
    parsed < AGENDA_DAYS_TO_DISPLAY
    ? parsed
    : 0;
};

const agendaDates = ref(buildAgendaDates());
agendaActiveIndex.value = allowedAgendaIndex(routeNonNegativeNumber('day', 0));
const activeAgendaDateKey = computed(
  () => agendaDates.value[agendaActiveIndex.value] || agendaDates.value[0]
);
const isActiveAgendaDateToday = computed(
  () => activeAgendaDateKey.value === localDateKey(new Date())
);
const activeAgendaDayLabel = computed(() =>
  new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
  }).format(new Date(`${activeAgendaDateKey.value}T00:00:00`))
);
const lastUpdatedAgendaLabel = computed(() => {
  if (!lastUpdatedAt.value) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(lastUpdatedAt.value);
});

const visitsLoadingState = computed(() => ({
  ...currentVisitBucket.value.loading,
  icon: 'i-lucide-loader-circle',
  iconClass: 'text-lais-primary',
  bucket: currentVisitBucket.value.value,
  fixture: currentVisitBucket.value.fixture,
  screenshot: currentVisitBucket.value.screenshot,
}));

const visitsEmptyState = computed(() => {
  if (loadError.value) {
    return {
      ...currentVisitBucket.value.error,
      icon: 'i-lucide-circle-alert',
      iconClass: 'text-rose-500',
      bucket: currentVisitBucket.value.value,
      fixture: currentVisitBucket.value.fixture,
      screenshot: currentVisitBucket.value.screenshot,
    };
  }

  if (
    hasActiveFilters.value &&
    (visitType.value !== 'all' ||
      (statusFilter.value && statusFilter.value !== 'todos') ||
      contactQuery.value ||
      imovelQuery.value ||
      userQuery.value ||
      exactDate.value ||
      startDate.value ||
      endDate.value)
  ) {
    return {
      title: t('VISITS.LIST.EMPTY_FILTERED_TITLE'),
      description: t('VISITS.LIST.EMPTY_FILTERED_DESCRIPTION'),
      icon: 'i-lucide-search-x',
      iconClass: 'text-lais-text-empty',
      bucket: currentVisitBucket.value.value,
      fixture: `${currentVisitBucket.value.fixture}-filtered`,
      screenshot: `${currentVisitBucket.value.screenshot}-filtered`,
    };
  }

  return {
    ...currentVisitBucket.value.empty,
    icon: 'i-lucide-calendar-check',
    iconClass: 'text-lais-text-empty',
    bucket: currentVisitBucket.value.value,
    fixture: currentVisitBucket.value.fixture,
    screenshot: currentVisitBucket.value.screenshot,
  };
});

const syncRouteQuery = () => {
  isSyncingRouteState = true;
  const visitTargetQuery = {
    visit_id: routeValue(route.query.visit_id),
    visita_id: routeValue(route.query.visita_id),
    scheduling_id: routeValue(route.query.scheduling_id),
    open_visit: routeValue(route.query.open_visit),
    flow: routeValue(route.query.flow),
    visitCode: routeValue(route.query.visitCode),
  };
  const viewQuery = isOperationalList.value
    ? {
        screen: routeString('screen'),
        tab: visitBucket.value,
        visit_bucket: visitBucket.value,
        business_context: businessContext.value,
        visit_type: visitType.value === 'all' ? '' : visitType.value,
        status: statusFilter.value === 'todos' ? '' : statusFilter.value,
        contact_query: contactQuery.value.trim(),
        imovel_query: imovelQuery.value.trim(),
        user_query: userQuery.value.trim(),
        contact_ids: contactIds.value,
        imovel_ids: imovelIds.value,
        user_id: userId.value,
        date: exactDate.value,
        start_date: startDate.value,
        end_date: endDate.value,
        sorted_by: sortOrder.value,
        page: page.value === 1 ? '' : page.value,
        per_page: perPage.value === 10 ? '' : perPage.value,
      }
    : {
        screen: routeString('screen'),
        view: isAgendaView.value ? '' : operationalView.value,
        day:
          isAgendaView.value && agendaActiveIndex.value > 0
            ? agendaActiveIndex.value
            : '',
      };

  router
    .replace({
      name: 'visitas_index',
      params: { accountId: accountId.value },
      query: compactQuery({
        ...visitTargetQuery,
        ...contextQueryFromRoute(),
        ...idFiltersFromRoute(),
        ...viewQuery,
        ...(!isOperationalList.value &&
        !isAgendaView.value &&
        hasOperationalPanelSection.value
          ? { section: routeString('section') }
          : {}),
      }),
    })
    .catch(() => {})
    .finally(() => {
      setTimeout(() => {
        isSyncingRouteState = false;
      }, 0);
    });
};

const applyRouteState = () => {
  page.value = routePositiveNumber('page', 1);
  perPage.value = routePositiveNumber('per_page', 10);
  visitBucket.value = allowedVisitBucket(
    routeString('tab') || routeString('visit_bucket') || visitBucketFromPath()
  );
  sortOrder.value = allowedSortOrder(
    routeString('sorted_by') ||
      routeString('sortedBy') ||
      routeString('order_by') ||
      routeString('orderBy') ||
      defaultSortForBucket(visitBucket.value)
  );
  operationalView.value = allowedOperationalView(
    routeString('view') || operationalView.value || operationalViewFromPath()
  );
  agendaActiveIndex.value = allowedAgendaIndex(
    routeNonNegativeNumber('day', 0)
  );
  visitType.value = routeString('visit_type', 'all');
  statusFilter.value = routeString('status', 'todos');
  businessContext.value =
    routeString('business_context') || routeString('businessContext');
  contactQuery.value = routeString('contact_query');
  imovelQuery.value = routeString('imovel_query');
  userQuery.value = routeString('user_query');
  contactIds.value = sanitizeIdList(route.query.contact_ids);
  imovelIds.value = sanitizeIdList(route.query.imovel_ids);
  userId.value = routeString('user_id');

  const routeDate = routeString('date');
  const routeStartDate = routeString('start_date');
  const routeEndDate = routeString('end_date');
  exactDate.value = isIsoDate(routeDate) ? routeDate : '';
  startDate.value = isIsoDate(routeStartDate) ? routeStartDate : '';
  endDate.value = isIsoDate(routeEndDate) ? routeEndDate : '';
  if (startDate.value && endDate.value && startDate.value > endDate.value) {
    startDate.value = '';
    endDate.value = '';
  }
  selected.value.clear();
};

let syncVisitDetailsFromRoute;
let openQuickActions;

const fetchVisitas = async ({ updateRoute = true } = {}) => {
  if (!canViewVisits.value) {
    loadError.value = false;
    capabilityError.value = { status: 403 };
    return;
  }

  isLoading.value = true;
  try {
    if (exactDate.value && !isIsoDate(exactDate.value)) exactDate.value = '';
    if (startDate.value && !isIsoDate(startDate.value)) startDate.value = '';
    if (endDate.value && !isIsoDate(endDate.value)) endDate.value = '';
    if (startDate.value && endDate.value && startDate.value > endDate.value) {
      startDate.value = '';
      endDate.value = '';
      useAlert('Período inválido. Ajuste as datas para continuar.');
    }
    if (updateRoute) syncRouteQuery();
    const params = {
      page: page.value,
      per_page: perPage.value,
      visit_bucket: visitBucket.value,
      sorted_by: sortOrder.value,
    };
    if (businessContext.value) params.business_context = businessContext.value;
    if (visitType.value !== 'all') params.visit_type = visitType.value;
    if (statusFilter.value && statusFilter.value !== 'todos') {
      params.status = statusFilter.value;
    }
    if (contactQuery.value) params.contact_query = contactQuery.value;
    if (imovelQuery.value) params.imovel_query = imovelQuery.value;
    if (userQuery.value) params.user_query = userQuery.value;
    if (contactIds.value) params.contact_ids = contactIds.value;
    if (imovelIds.value) params.imovel_ids = imovelIds.value;
    if (userId.value) params.user_id = userId.value;
    if (exactDate.value) params.date = exactDate.value;
    if (startDate.value) params.start_date = startDate.value;
    if (endDate.value) params.end_date = endDate.value;
    await store.get(params);
    lastUpdatedAt.value = new Date();
    loadError.value = false;
    capabilityError.value = null;
    await syncVisitDetailsFromRoute();
    trackReferenceEvent(
      visitBucket.value === 'past'
        ? 'finished_schedulings_page_viewed'
        : 'agenda_visit_list_page_viewed',
      {
        page: page.value,
        per_page: perPage.value,
        visit_type: visitType.value,
        status: statusFilter.value,
        visits_count: visitas.value.length,
        bucket_fixture: currentVisitBucket.value.fixture,
        bucket_screenshot: currentVisitBucket.value.screenshot,
      }
    );
    if (visitBucket.value !== 'past') {
      trackReferenceEvent(SCHEDULED_VISITS_TRACKING_EVENTS.PAGE_VIEWED, {
        page: page.value,
        visits_count: visitas.value.length,
      });
      if (contactQuery.value || imovelQuery.value || userQuery.value) {
        trackReferenceEvent(
          SCHEDULED_VISITS_TRACKING_EVENTS.SEARCH_TYPED,
          scheduledVisitSearchTrackingPayload()
        );
      }
    }
  } catch (error) {
    loadError.value = true;
    capabilityError.value = error;
    useAlert(
      `${t('VISITS.ERRORS.LOAD_TITLE')}. ${t('VISITS.ERRORS.LOAD_DESCRIPTION')}`
    );
    trackReferenceEvent('agenda_visit_detail_alert_status_confirmed', {
      reason: 'fetch_error',
      bucket_fixture: currentVisitBucket.value.fixture,
      bucket_screenshot: currentVisitBucket.value.screenshot,
    });
  } finally {
    isLoading.value = false;
  }
};

const fetchAgendaVisits = async ({ updateRoute = true } = {}) => {
  if (!canViewVisits.value) {
    loadError.value = false;
    capabilityError.value = { status: 403 };
    return;
  }

  const selectedDate = activeAgendaDateKey.value;
  if (!selectedDate || !businessContext.value) return;

  isLoading.value = true;
  try {
    if (updateRoute) syncRouteQuery();
    await store.get({
      page: 1,
      per_page: 100,
      visit_bucket: 'upcoming',
      date: selectedDate,
      start_date: selectedDate,
      end_date: selectedDate,
      business_context: businessContext.value,
    });
    lastUpdatedAt.value = new Date();
    loadError.value = false;
    capabilityError.value = null;
    await syncVisitDetailsFromRoute();
    trackReferenceEvent('agenda_visit_list_page_viewed', {
      date: selectedDate,
      day: agendaActiveIndex.value,
      visits_count: visitas.value.length,
    });
    trackReferenceEvent(SCHEDULED_VISITS_TRACKING_EVENTS.PAGE_VIEWED, {
      date: selectedDate,
      visits_count: visitas.value.length,
    });
  } catch (error) {
    loadError.value = true;
    capabilityError.value = error;
    useAlert(
      `${t('VISITS.ERRORS.LOAD_TITLE')}. ${t('VISITS.ERRORS.LOAD_DESCRIPTION')}`
    );
    trackReferenceEvent('agenda_visit_detail_alert_status_confirmed', {
      reason: 'agenda_fetch_error',
      date: selectedDate,
    });
  } finally {
    isLoading.value = false;
  }
};

const applyFilters = (trigger = 'filters') => {
  const trackingTrigger = typeof trigger === 'string' ? trigger : 'filters';
  page.value = 1;
  trackVisitIndexFilterApplied(trackingTrigger);
  fetchVisitas();
};
const lastUpdatedLabel = computed(() => {
  if (!lastUpdatedAt.value) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(lastUpdatedAt.value);
});

const selectVisitType = value => {
  visitType.value = value;
  applyFilters('visit_type');
};

selectVisitBucket = value => {
  visitBucket.value = value;
  statusFilter.value = 'todos';
  sortOrder.value = defaultSortForBucket(value);
  selected.value.clear();
  trackReferenceEvent('agenda_day_tab_changed', {
    tab: value,
    bucket_fixture: currentVisitBucket.value.fixture,
    bucket_screenshot: currentVisitBucket.value.screenshot,
  });
  applyFilters('visit_bucket');
};

const selectSortOrder = () => {
  page.value = 1;
  trackVisitIndexFilterApplied('sort_order');
  fetchVisitas();
};

const selectAgendaDay = index => {
  const nextIndex = allowedAgendaIndex(index);
  if (nextIndex === agendaActiveIndex.value) return;

  agendaActiveIndex.value = nextIndex;
  selected.value.clear();
  trackReferenceEvent('agenda_day_tab_changed', {
    day: nextIndex,
    date: activeAgendaDateKey.value,
  });
  fetchAgendaVisits();
};

const selectOperationalView = value => {
  operationalView.value = allowedOperationalView(value);
  selected.value.clear();
  showFilters.value = false;
  if (operationalView.value === 'blocks') {
    trackReferenceEvent('time_block_page_viewed');
  }
  if (operationalView.value === 'keys') {
    trackReferenceEvent('entrance_info_page_viewed', { source: 'keys_tab' });
  }

  if (isOperationalList.value) {
    fetchVisitas();
  } else if (isAgendaView.value) {
    fetchAgendaVisits();
  } else {
    syncRouteQuery();
  }
};

const clearFilters = () => {
  visitType.value = 'all';
  statusFilter.value = 'todos';
  sortOrder.value = defaultSortForBucket(visitBucket.value);
  contactQuery.value = '';
  imovelQuery.value = '';
  userQuery.value = '';
  exactDate.value = '';
  startDate.value = '';
  endDate.value = '';
  applyFilters('clear_filters');
};

const novaVisita = () => {
  if (!canCreateVisitAction.value) return;

  showCreateVisitModal.value = true;
  trackReferenceEvent('new_visit_modal_opened');
  trackReferenceEvent('agenda_new_visit_clicked');
};

const createAutomationForVisitEvent = eventName => {
  if (!isAdmin.value || !visitAutomationEventNames.has(eventName)) return;

  showAutomationMenu.value = false;
  router.push({
    name: 'automation_list',
    params: { accountId: accountId.value },
    query: {
      create: '1',
      automation_event: eventName,
    },
  });
};

const handleAutomationMenuAction = ({ action, value }) => {
  if (action !== 'create_automation') return;
  createAutomationForVisitEvent(value);
};

const startVisitCreation = context => {
  if (!canCreateVisitAction.value) return;

  showCreateVisitModal.value = false;
  trackReferenceEvent('new_visit_pageview', { context });
  router.push(
    frontendURL(`accounts/${accountId.value}/visitas/new?context=${context}`)
  );
};

const editarVisita = id => {
  if (!canRescheduleVisits.value) return;

  router.push(frontendURL(`accounts/${accountId.value}/visitas/${id}/edit`));
};

const abrirDetalheVisita = id =>
  router.push(frontendURL(`accounts/${accountId.value}/visitas/${id}`));

const revisarVisita = id => {
  if (!canReviewVisitsAction.value) return;

  router.push(frontendURL(`accounts/${accountId.value}/visitas/${id}/revisar`));
};

const canFinishVisit = visita =>
  canFinishVisits.value &&
  !['realizada', 'nao_compareceu', 'nao_sucedida', 'cancelada'].includes(
    visita?.status
  );

const visitCardStatusOptions = computed(() => {
  const features = currentAccount.value?.features || {};
  const settings = currentAccount.value?.settings || {};

  return {
    isVisitConfirmationByAgentEnabled:
      features.visit_confirmation_by_agent === true ||
      features.visitConfirmationByAgent === true ||
      settings.visit_confirmation_by_agent === true ||
      settings.visitConfirmationByAgent === true ||
      currentAccount.value?.laisVisitConfirmationRequired === true ||
      currentAccount.value?.lais_visit_confirmation_required === true,
    isVisitStatusUnavailableHoursEnabled:
      features.visit_status_unavailable_hours !== false &&
      features.visitStatusUnavailableHours !== false &&
      settings.visit_status_unavailable_hours !== false &&
      settings.visitStatusUnavailableHours !== false,
  };
});

function cardStatus(visita) {
  return (
    getVisitaCardStatus(visita, new Date(), {
      ...visitCardStatusOptions.value,
      isThirdParty:
        visita?.isThirdParty ||
        visita?.is_third_party ||
        visita?.imovel?.listingSource === 'third_party' ||
        visita?.imovel?.listing_source === 'third_party',
    }) || visita?.status
  );
}

function visitStatusToken(visita) {
  return normalizedToken(
    firstPresent(
      visita?.status_v3,
      visita?.statusV3,
      visita?.status,
      visitValue(visita, 'flowStatus')
    )
  );
}

const terminalVisitCardStatuses = new Set([
  VISITA_CARD_STATUS.DONE,
  VISITA_CARD_STATUS.CANCELED,
  VISITA_CARD_STATUS.NOT_HAPPENED,
  VISITA_CARD_STATUS.UNSUCCESSFUL,
]);
const terminalVisitStatusTokens = new Set([
  'cancelada',
  'canceled',
  'cancelled',
  'realizada',
  'done',
  'nao_compareceu',
  'not_happened',
  'nao_sucedida',
  'unsuccessful',
]);

function isTerminalVisit(visita) {
  return (
    terminalVisitCardStatuses.has(cardStatus(visita)) ||
    terminalVisitStatusTokens.has(visitStatusToken(visita))
  );
}

function shouldShowConfirmCTA(visita) {
  return (
    canConfirmVisits.value &&
    !isVisitExpired(visita) &&
    (cardStatus(visita) === VISITA_CARD_STATUS.WAITING_CONFIRMATION ||
      visita?.status === 'solicitada')
  );
}

function canCancelVisitAction(visita) {
  return (
    canCancelVisits.value &&
    Boolean(visita?.id) &&
    !isVisitExpired(visita) &&
    !isTerminalVisit(visita)
  );
}

function canRescheduleVisitAction(visita) {
  return (
    canRescheduleVisits.value &&
    Boolean(visita?.id) &&
    !isVisitExpired(visita) &&
    !isTerminalVisit(visita)
  );
}

function canFinishVisitNow(visita) {
  if (!canFinishVisits.value) return false;

  const scheduledTime = visitTime(visita);
  return !scheduledTime || scheduledTime <= Date.now();
}

function canFollowUpVisitAction(visita) {
  return canFinishVisit(visita) && canFinishVisitNow(visita);
}

function canAddMoreVisitsAction(visita) {
  return canCreateVisitAction.value && Boolean(visita?.id);
}

function trackVisitIndexAction(action, visita, extras = {}) {
  if (!visita?.id) return;

  trackReferenceEvent(VISIT_TRACKING_EVENTS.DETAIL_ACTION_CLICKED, {
    ...visitFlowTrackingPayload(visita, {
      action,
      action_origin: 'visitas_index',
      card_status: cardStatus(visita),
      bucket_fixture: currentVisitBucket.value.fixture,
      bucket_screenshot: currentVisitBucket.value.screenshot,
      ...extras,
    }),
  });
}

const openFinishVisitModal = visita => {
  if (!canFinishVisits.value) return;
  if (!canFollowUpVisitAction(visita)) return;

  finishVisitCandidate.value = visita;
  showFinishVisitModal.value = true;
};

const finalizarVisita = outcome => {
  if (!canFinishVisits.value) return;

  const candidate = finishVisitCandidate.value;
  const visitId = candidate?.id;
  if (!visitId) return;

  showFinishVisitModal.value = false;
  trackReferenceEvent(VISIT_TRACKING_EVENTS.FINALIZATION_DIALOG_ACTION, {
    ...visitFlowTrackingPayload(candidate),
    user_action: visitFinalizationUserAction(outcome),
    visit_code: candidate?.visitCode || candidate?.visit_code || visitId,
    house_id: propertyId(candidate) || candidate?.imovel?.id,
  });
  router.push(
    frontendURL(
      `accounts/${accountId.value}/visitas/${visitId}/finalizar?outcome=${outcome}`
    )
  );
};

const visitAddressText = visita =>
  [visita?.imovel?.endereco, visita?.imovel?.bairro, visita?.imovel?.cidade]
    .filter(Boolean)
    .join(', ') ||
  propertyAddress(visita) ||
  '—';

const quickDateLabel = iso =>
  new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));

const quickTimeLabel = iso =>
  new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));

const quickVisitAddress = computed(() =>
  visitAddressText(activeQuickVisit.value)
);
const quickVisitDateText = computed(() => {
  if (!activeQuickVisit.value?.scheduledAt) return '—';
  if (visitSummary(activeQuickVisit.value).scheduledAtLabel) {
    return visitSummary(activeQuickVisit.value).scheduledAtLabel;
  }

  return `${quickDateLabel(activeQuickVisit.value.scheduledAt)}, às ${quickTimeLabel(activeQuickVisit.value.scheduledAt)}`;
});
const visitorCheckInSource = visita =>
  [visita?.contact, visita?.visitor, visita].find(
    item =>
      item?.checkInStatus ||
      item?.check_in_status ||
      item?.checkinStatus ||
      item?.checkin_status
  ) ||
  visita?.contact ||
  visita;
const hasVisitorCheckIn = visita =>
  Boolean(visita?.contact?.name) &&
  isVisitaCheckInDone(visitorCheckInSource(visita));

const visitDetailsRouteIdentifier = () =>
  [
    route.query.visit_id,
    route.query.visita_id,
    route.query.scheduling_id,
    route.query.open_visit,
  ]
    .map(routeQueryString)
    .find(Boolean) || '';

const visitIdentifierMatches = (visita, identifier) => {
  if (!visita || !identifier) return false;
  const normalized = String(identifier);
  const attrs = visitAdditionalAttributes(visita);
  const summary = visitSummary(visita);
  return [
    visita.id,
    visita.visit_id,
    visita.visitId,
    visita.scheduling_id,
    visita.schedulingId,
    visita.public_token,
    visita.publicToken,
    visita.token,
    attrs.visit_code,
    attrs.visitCode,
    attrs.scheduling_id,
    attrs.schedulingId,
    attrs.public_token,
    attrs.publicToken,
    summary.visit_code,
    summary.visitCode,
  ]
    .filter(value => value !== undefined && value !== null && value !== '')
    .some(value => String(value) === normalized);
};

const replaceRouteQueryWithoutVisitTarget = () => {
  const {
    visit_id: _visitId,
    visita_id: _visitaId,
    scheduling_id: _schedulingId,
    open_visit: _openVisit,
    ...query
  } = route.query;
  isSyncingRouteState = true;
  router
    .replace({ query })
    .catch(() => {})
    .finally(() => {
      setTimeout(() => {
        isSyncingRouteState = false;
      }, 0);
    });
};

const replaceRouteQueryWithVisitTarget = visita => {
  if (!visita?.id) return;
  isSyncingRouteState = true;
  router
    .replace({
      query: compactQuery({
        ...route.query,
        visit_id: visita.id,
      }),
    })
    .catch(() => {})
    .finally(() => {
      setTimeout(() => {
        isSyncingRouteState = false;
      }, 0);
    });
};

syncVisitDetailsFromRoute = async () => {
  const identifier = visitDetailsRouteIdentifier();
  if (!identifier) return;
  if (
    showQuickActionsDrawer.value &&
    visitIdentifierMatches(quickActionsVisit.value, identifier)
  ) {
    return;
  }

  const existingVisit = visitas.value.find(visita =>
    visitIdentifierMatches(visita, identifier)
  );
  if (existingVisit) {
    openQuickActions(existingVisit, { syncRoute: false, source: 'deep_link' });
    return;
  }

  try {
    const visita = await store.show(identifier);
    const normalizedVisit =
      visitas.value.find(
        item =>
          visitIdentifierMatches(item, identifier) ||
          String(item.id) === String(visita?.id)
      ) || visita;
    openQuickActions(normalizedVisit, {
      syncRoute: false,
      source: 'deep_link_fetch',
    });
  } catch {
    useAlert('Não foi possível abrir a visita solicitada.');
  }
};

openQuickActions = (visita, { syncRoute = true, source = 'card' } = {}) => {
  quickActionsVisit.value = visita;
  showQuickActionsDrawer.value = true;
  if (syncRoute) replaceRouteQueryWithVisitTarget(visita);
  const payload = visitFlowTrackingPayload(visita, {
    source,
    card_status: cardStatus(visita),
    bucket_fixture: currentVisitBucket.value.fixture,
    bucket_screenshot: currentVisitBucket.value.screenshot,
  });
  const openedVisitStatus = normalizedToken(
    firstPresent(
      visita?.status_v3,
      visita?.status,
      visitValue(visita, 'flowStatus')
    )
  );
  trackReferenceEvent('agenda_visit_card_clicked', payload);
  trackReferenceEvent('agenda_visit_detail_page_viewed', payload);
  trackVisitIndexAction('open_quick_actions_drawer', visita, {
    source,
  });
  if (['stalled', 'paralisada'].includes(openedVisitStatus)) {
    trackReferenceEvent('visit_stalled', payload);
  }
  if (['unsuccessful', 'nao_sucedida'].includes(openedVisitStatus)) {
    trackReferenceEvent('visit_unsuccessful', payload);
  }
  if (visitBucket.value === 'past') {
    trackReferenceEvent('finished_schedulings_card_opened', payload);
  } else {
    trackReferenceEvent(SCHEDULED_VISITS_TRACKING_EVENTS.EXPAND_CARD, payload);
  }
};

const closeQuickActions = () => {
  showQuickActionsDrawer.value = false;
  quickActionsVisit.value = null;
  replaceRouteQueryWithoutVisitTarget();
};

const visitConversationId = visit =>
  visit?.conversationId || visit?.conversation_id || visit?.conversation?.id;

const visitInboxId = visit =>
  visit?.inboxId ||
  visit?.inbox_id ||
  visit?.contact?.inboxId ||
  visit?.contact?.inbox_id;

const resolveVisitChannel = ({ visit, message, preferDialer = false }) =>
  resolveChannel({
    accountId: accountId.value,
    phone: contactPhone(visit),
    text: message,
    preferDialer,
    conversationId: visitConversationId(visit),
    contactId: visit?.contact?.id || visit?.contactId,
    inboxId: visitInboxId(visit),
  });

const openQuickChannel = (resolvedChannel, successMessage) => {
  if (resolvedChannel.channel === 'error') {
    useAlert(channelErrorMessage(resolvedChannel));
    return false;
  }

  const opened = openResolvedChannel({
    resolvedChannel,
    router,
  });
  useAlert(opened ? successMessage : channelErrorMessage(resolvedChannel));
  return opened;
};

const openVisitMessage = visit => {
  const resolvedChannel = resolveVisitChannel({
    visit,
    message: `Olá${contactName(visit) ? `, ${contactName(visit)}` : ''}!`,
  });
  const opened = openQuickChannel(
    resolvedChannel,
    'Conversa do cliente aberta.'
  );
  trackVisitIndexAction('send_message', visit, {
    ...channelTrackingPayload(resolvedChannel),
    opened,
  });
  trackReferenceEvent('agenda_visit_detail_send_message_clicked', {
    ...visitFlowTrackingPayload(visit),
    ...channelTrackingPayload(resolvedChannel),
    opened,
  });
  trackReferenceEvent('chatcrpp_access_chat_clicked', {
    ...visitFlowTrackingPayload(visit),
    opened,
  });
  trackReferenceEvent(SCHEDULED_VISITS_TRACKING_EVENTS.SEND_MESSAGE, {
    ...visitFlowTrackingPayload(visit),
    opened,
  });
};

const callVisitContact = visit => {
  const resolvedChannel = resolveVisitChannel({
    visit,
    preferDialer: true,
  });
  const opened = openQuickChannel(
    resolvedChannel,
    'Ligação do cliente aberta.'
  );
  trackVisitIndexAction('call_visit_contact', visit, {
    ...channelTrackingPayload(resolvedChannel),
    opened,
  });
  trackReferenceEvent('call_living_tenant', {
    ...visitFlowTrackingPayload(visit),
    opened,
  });
};

const openAgentActivity = () => {
  selectOperationalView('agenda');
};

const openKeyHolderInfoDialog = (visit = activeQuickVisit.value) => {
  if (visit?.id) quickActionsVisit.value = visit;
  showAgendaInfoModal.value = false;
  showKeyHolderInfoDialog.value = true;
  trackVisitIndexAction('open_key_holder_info', visit);
};

const closeVisitConfirmation = () => {
  showVisitConfirmationModal.value = false;
  showVisitConfirmationSuccess.value = false;
  visitConfirmationLoading.value = false;
  confirmationVisit.value = null;
};

const openVisitConfirmation = visit => {
  if (!canConfirmVisits.value) return;
  if (!visit?.id) return;
  if (isVisitExpired(visit)) {
    useAlert(VISIT_CONFIRM_EXPIRED_MESSAGE);
    return;
  }
  if (!shouldShowConfirmCTA(visit)) return;

  trackVisitIndexAction('open_confirm_visit', visit);
  trackReferenceEvent('initiate_visit_confirmation_button_clicked', {
    ...visitFlowTrackingPayload(visit),
  });
  trackReferenceEvent('confirm_visit_page_viewed', {
    ...visitFlowTrackingPayload(visit),
  });
  confirmationVisit.value = visit;
  showVisitConfirmationSuccess.value = false;
  showQuickActionsDrawer.value = false;
  showVisitConfirmationModal.value = true;
};

const confirmVisitFromPanel = async () => {
  if (!canConfirmVisits.value) return;

  const visit = confirmationVisit.value;
  if (!visit?.id) return;

  visitConfirmationLoading.value = true;
  trackVisitIndexAction('submit_confirm_visit', visit);
  trackReferenceEvent('confirm_visit_button_clicked', {
    ...visitFlowTrackingPayload(visit),
  });
  await store.update({
    id: visit.id,
    status: 'confirmada',
    additional_attributes: quickActionAttributes({
      action: AGENDA_QUICK_ACTIONS.CONFIRM,
    }),
  });
  visitConfirmationLoading.value = false;
  showVisitConfirmationSuccess.value = true;
  useAlert('Pronto, visita confirmada!');
  trackReferenceEvent('visit_confirmed', {
    ...visitFlowTrackingPayload(visit),
  });
  await fetchVisitas();
};

const finishVisitConfirmation = () => {
  closeVisitConfirmation();
  syncRouteQuery();
};

const agentCanActOnVisitHouse = visit =>
  isAgentAllowedForHouse(
    visit?.imovel,
    currentUser.value?.business_contexts ||
      currentAccount.value?.business_contexts
  );

const markQuickAction = async (action, extra = {}) => {
  if (!activeQuickVisit.value?.id) return;

  await store.update({
    id: activeQuickVisit.value.id,
    additional_attributes: quickActionAttributes({ action, extra }),
  });
};

const contactIdForVisit = visit => visit?.contact?.id || visit?.contactId;

const offerActionLabel = visit =>
  actionCompleted(visit, AGENDA_QUICK_ACTIONS.OFFER)
    ? 'Reenviar link de proposta'
    : 'Enviar link de proposta';

const doneOfferActionLabel = () => 'Encaminhar link da proposta';

const registerOfferLinkForVisit = async visit => {
  const imovelId = propertyId(visit);
  const contactId = contactIdForVisit(visit);
  if (!imovelId || !contactId) {
    useAlert('Informe cliente vinculado e imóvel antes de enviar a proposta.');
    return null;
  }

  const { data } = await PropertyOffersAPI.sendOfferLink({
    payload: {
      houseId: imovelId,
      visitorId: contactId,
    },
  });
  return data;
};

const sendQuickIntroduction = async () => {
  if (!canUseQuickVisitMessages.value) return;

  const visit = activeQuickVisit.value;
  if (!canSendIntroduction(visit)) {
    useAlert(
      'Informe telefone do cliente e endereço do imóvel antes de enviar.'
    );
    return;
  }

  const resolvedChannel = resolveVisitChannel({
    visit,
    message: isAgentSupportInProgress(visit)
      ? buildConfirmVisitMessage({ visit })
      : buildIntroductionMessage({
          visit,
          agentName: currentUser.value?.name || 'Imudei',
          companyName: currentAccount.value?.name || 'Imudei',
        }),
  });
  const opened = openQuickChannel(
    resolvedChannel,
    isAgentSupportInProgress(visit)
      ? 'Confirmação da visita aberta.'
      : 'Apresentação aberta.'
  );
  trackVisitIndexAction('send_introduction', visit, {
    ...channelTrackingPayload(resolvedChannel),
    opened,
  });
  trackReferenceEvent('whtsppciqtpa_agentspwa_pagevisita_clicked', {
    ...visitFlowTrackingPayload(visit),
    ...channelTrackingPayload(resolvedChannel),
    opened,
  });
  await markQuickAction(AGENDA_QUICK_ACTIONS.INTRODUCTION, {
    ...channelTrackingPayload(resolvedChannel),
    message_type: isAgentSupportInProgress(visit)
      ? 'confirm_visit'
      : 'introduction',
  });
};

const sendQuickOffer = async () => {
  if (!canSendVisitOffer.value) return;

  const visit = activeQuickVisit.value;
  if (!canSendOffer(visit)) {
    useAlert('Informe cliente, telefone e imóvel antes de enviar a proposta.');
    return;
  }

  let offerLinkRecord = null;
  try {
    offerLinkRecord = await registerOfferLinkForVisit(visit);
  } catch {
    useAlert('Não foi possível registrar o envio da proposta.');
    return;
  }
  if (!offerLinkRecord) return;

  const resolvedChannel = resolveVisitChannel({
    visit,
    message: buildOfferMessage({
      accountId: accountId.value,
      visit,
      origin: typeof window === 'undefined' ? '' : window.location.origin,
    }),
  });
  const opened = openQuickChannel(
    resolvedChannel,
    offerLinkRecord.already_sent
      ? 'Reenvio de proposta aberto.'
      : 'Link de proposta aberto.'
  );
  trackVisitIndexAction('send_offer_link', visit, {
    ...channelTrackingPayload(resolvedChannel),
    opened,
    offer_link_already_sent: offerLinkRecord.already_sent,
  });
  trackReferenceEvent(opened ? 'offer_link_success' : 'offer_link_failure', {
    ...visitFlowTrackingPayload(visit),
    ...channelTrackingPayload(resolvedChannel),
  });
  trackReferenceEvent('finished_schedulings_card_send_proposal_clicked', {
    ...visitFlowTrackingPayload(visit),
    opened,
  });
  await markQuickAction(AGENDA_QUICK_ACTIONS.OFFER, {
    ...channelTrackingPayload(resolvedChannel),
    imovel_id: propertyId(visit),
    contact_id: contactIdForVisit(visit),
    offer_link_event_id: offerLinkRecord.event_id,
    offer_link_already_sent: offerLinkRecord.already_sent,
    offer_link_deep_link: offerLinkRecord.deep_link,
  });
};

const sendOfferForVisit = async visit => {
  if (!canSendVisitOffer.value) return;

  quickActionsVisit.value = visit;
  await sendQuickOffer();
};

const suggestQuickHouse = async () => {
  if (!canUseQuickVisitMessages.value) return;

  const visit = activeQuickVisit.value;
  if (!canSuggestHouse(visit)) {
    useAlert('Informe cliente, telefone e imóvel antes de sugerir o imóvel.');
    return;
  }
  if (!agentCanActOnVisitHouse(visit)) {
    useAlert('Este corretor não está habilitado para sugerir este imóvel.');
    return;
  }

  const resolvedChannel = resolveVisitChannel({
    visit,
    message: buildVisitSuggestHouseMessage({
      accountId: accountId.value,
      visit,
      origin: typeof window === 'undefined' ? '' : window.location.origin,
    }),
  });
  const opened = openQuickChannel(
    resolvedChannel,
    'Sugestão de imóvel aberta.'
  );
  trackVisitIndexAction('suggest_house', visit, {
    ...channelTrackingPayload(resolvedChannel),
    opened,
  });
  trackReferenceEvent('agenda_visit_detail_suggestion_clicked', {
    ...visitFlowTrackingPayload(visit),
    ...channelTrackingPayload(resolvedChannel),
    opened,
  });
  await markQuickAction(AGENDA_QUICK_ACTIONS.SUGGEST_HOUSE, {
    ...channelTrackingPayload(resolvedChannel),
    imovel_id: propertyId(visit),
  });
};

const rescheduleQuickVisit = () => {
  const visit = activeQuickVisit.value;
  if (!canRescheduleVisitAction(visit)) return;

  const visitId = visit?.id;
  if (!visitId) return;

  trackVisitIndexAction('reschedule_visit', visit);
  trackReferenceEvent('agenda_visit_dialog_reschedule_clicked', {
    ...visitFlowTrackingPayload(visit),
  });
  showWantToCancelModal.value = false;
  closeQuickActions();
  router.push(
    frontendURL(
      `accounts/${accountId.value}/visitas/${visitId}/edit?context=reschedule`
    )
  );
};

const rescheduleVisit = visit => {
  quickActionsVisit.value = visit;
  rescheduleQuickVisit();
};

const visitorInfoBusinessContext = visita =>
  visita?.imovel?.finalidade === 'venda' ? 'for_sale' : 'for_rent';

const openVisitorInfo = visita => {
  const contactId = visita?.contact?.id || visita?.contactId;
  if (!contactId) {
    useAlert('Cliente não informado para esta visita.');
    return;
  }

  trackReferenceEvent('visitorinfo_page_viewed', {
    ...visitFlowTrackingPayload(visita),
    contact_id: contactId,
  });
  trackVisitIndexAction('open_visitor_profile', visita, {
    contact_id: contactId,
  });
  trackReferenceEvent(SCHEDULED_VISITS_TRACKING_EVENTS.VIEW_PROFILE, {
    ...visitFlowTrackingPayload(visita),
    contact_id: contactId,
  });
  closeQuickActions();
  router.push(
    frontendURL(`accounts/${accountId.value}/visitas/visitante/${contactId}`, {
      business_context: visitorInfoBusinessContext(visita),
      backTo: route.fullPath,
    })
  );
};

const startQuickSupport = () => {
  const visit = activeQuickVisit.value;
  if (!visit?.id) return;

  const supportId = agentSupportId(visit);
  trackVisitIndexAction('start_support', visit, {
    support_id: supportId || null,
  });
  trackReferenceEvent('agenda_start_support_clicked', {
    ...visitFlowTrackingPayload(visit),
    support_id: supportId || null,
  });

  if (!supportId) {
    openVisitorInfo(visit);
    return;
  }

  showQuickActionsDrawer.value = false;
  quickActionsVisit.value = null;
  router
    .replace({
      query: compactQuery({
        ...route.query,
        atendimento: supportId,
        visit_id: visit.id,
      }),
    })
    .catch(() => {});
  useAlert('Atendimento aberto.');
};

const openWantToCancel = () => {
  const visit = activeQuickVisit.value;
  if (!canCancelVisitAction(visit)) return;

  trackVisitIndexAction('open_cancel_confirmation', visit);
  showWantToCancelModal.value = true;
};

const openCardCancel = visit => {
  if (!canCancelVisitAction(visit)) return;

  quickActionsVisit.value = visit;
  openWantToCancel();
};

const talkToOwner = async () => {
  const visit = activeQuickVisit.value;
  trackVisitIndexAction('talk_to_owner', visit, {
    can_talk_to_owner: canTalkToOwner(visit),
  });
  if (!canTalkToOwner(visit)) {
    agendaInfoKind.value = 'responsible';
    showAgendaInfoModal.value = true;
    return;
  }

  const resolvedChannel = resolveOwnerChannel({
    accountId: accountId.value,
    visit,
    accountConfig: {
      ownerContactChannel:
        currentAccount.value?.settings?.lais_owner_contact_channel ||
        currentAccount.value?.settings?.laisOwnerContactChannel ||
        currentAccount.value?.custom_attributes?.lais_owner_contact_channel ||
        currentAccount.value?.custom_attributes?.laisOwnerContactChannel ||
        currentAccount.value?.custom_attributes?.owner_contact_channel ||
        currentAccount.value?.settings?.owner_contact_channel,
    },
    text: `Olá! Quero alinhar a visita do cliente ${contactName(
      visit
    )} para ${propertyAddress(visit) || 'o imóvel'}.`,
  });
  const opened = openQuickChannel(
    resolvedChannel,
    'Conversa com proprietário aberta.'
  );
  trackReferenceEvent('owner_contact_open_clicked', {
    ...visitFlowTrackingPayload(visit),
    ...channelTrackingPayload(resolvedChannel),
    opened,
  });
  trackReferenceEvent('owner_contact_send_message_clicked', {
    ...visitFlowTrackingPayload(visit),
    opened,
  });
  trackReferenceEvent('chatcrpp_access_chat_lr_clicked', {
    ...visitFlowTrackingPayload(visit),
    opened,
  });
  await markQuickAction(AGENDA_QUICK_ACTIONS.OWNER, {
    ...channelTrackingPayload(resolvedChannel),
  });
};

const followUpQuickVisit = () => {
  const visit = activeQuickVisit.value;
  if (!canFollowUpVisitAction(visit)) return;

  trackVisitIndexAction('finish_visit', visit);
  closeQuickActions();
  openFinishVisitModal(visit);
};

const addMoreVisits = () => {
  if (!canAddMoreVisitsAction(activeQuickVisit.value)) return;

  const imovelId = propertyId(activeQuickVisit.value);
  const query = imovelId ? `?context=schedule&imovel_id=${imovelId}` : '';

  trackVisitIndexAction('add_more_visits', activeQuickVisit.value, {
    has_imovel_id: Boolean(imovelId),
  });
  closeQuickActions();
  router.push(frontendURL(`accounts/${accountId.value}/visitas/new${query}`));
};

const openAgendaInfo = kind => {
  trackVisitIndexAction('open_agenda_info', activeQuickVisit.value, {
    info_kind: kind,
  });
  agendaInfoKind.value = kind;
  showAgendaInfoModal.value = true;
};

const goToQuickVisitEntryEdit = () => {
  const imovelId = propertyId(activeQuickVisit.value);
  if (!imovelId) return;

  trackVisitIndexAction('edit_entry_information', activeQuickVisit.value, {
    imovel_id: imovelId,
  });
  router.push(
    frontendURL(`accounts/${accountId.value}/imoveis/${imovelId}/edit`)
  );
};

const quickActionCompletedLabel = action =>
  actionCompleted(activeQuickVisit.value, action) ? 'Reenviar' : 'Enviar';

const agendaInfoTitle = computed(
  () =>
    ({
      responsible: 'Responsável pelo imóvel',
      learnMore: 'Saiba mais sobre esta visita',
      keyHolder: 'Entrada e chaves',
    })[agendaInfoKind.value] || 'Informações da agenda'
);

const abrirComoFunciona = () => {
  if (typeof window !== 'undefined' && typeof window.Intercom === 'function') {
    window.Intercom('showNewMessage', 'Como funciona a visita na Imudei?');
  } else {
    window.open('/lais/suporte', '_blank', 'noopener');
  }
};

const isoDate = date => date.toISOString().slice(0, 10);

const addDays = days => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return isoDate(date);
};

const formattedDate = value => {
  if (!value) return '—';

  return new Intl.DateTimeFormat('pt-BR').format(new Date(value));
};

const formatDate = iso =>
  new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));

const formatTime = iso =>
  new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
const selectedVisitAddress = computed(() =>
  [
    selectedVisit.value?.imovel?.endereco,
    selectedVisit.value?.imovel?.bairro,
    selectedVisit.value?.imovel?.cidade,
  ]
    .filter(Boolean)
    .join(', ')
);

const selectedVisitDateText = computed(() => {
  if (!selectedVisit.value?.scheduledAt) return '—';
  if (visitSummary(selectedVisit.value).scheduledAtLabel) {
    return visitSummary(selectedVisit.value).scheduledAtLabel;
  }

  return `${formatDate(selectedVisit.value.scheduledAt)}, às ${formatTime(selectedVisit.value.scheduledAt)}`;
});
const finishVisitAddress = computed(() =>
  [
    finishVisitCandidate.value?.imovel?.endereco,
    finishVisitCandidate.value?.imovel?.bairro,
    finishVisitCandidate.value?.imovel?.cidade,
  ]
    .filter(Boolean)
    .join(', ')
);
const finishVisitDateText = computed(() => {
  if (!finishVisitCandidate.value?.scheduledAt) return '—';
  if (visitSummary(finishVisitCandidate.value).scheduledAtLabel) {
    return visitSummary(finishVisitCandidate.value).scheduledAtLabel;
  }

  return `${formatDate(finishVisitCandidate.value.scheduledAt)}, às ${formatTime(finishVisitCandidate.value.scheduledAt)}`;
});
const selectedVisitSummary = computed(() => {
  if (!selectedVisit.value) return null;

  return {
    address: selectedVisitAddress.value || null,
    client: selectedVisit.value.contact?.name || null,
    date: selectedVisitDateText.value,
    agent: selectedVisit.value.user?.name || null,
    imovel_id: selectedVisit.value.imovel?.id || selectedVisit.value.imovelId,
    imovel_title: selectedVisit.value.imovel?.titulo || null,
  };
});
const activeAvailabilityDay = computed(
  () => availabilityDays.value[selectedAvailabilityDay.value] || null
);

const suspensionAlert = computed(() =>
  cancelForm.suspendListing && cancelForm.suspensionEndsAt
    ? republishAlertMessage(formattedDate(cancelForm.suspensionEndsAt))
    : ''
);
const cancelConfirmLabel = computed(() =>
  cancelForm.suspendListing
    ? VISIT_CANCEL_SUSPEND_CONFIRM_LABEL
    : VISIT_CANCEL_CONFIRM_LABEL
);
const cancelSuccessTitle = computed(() =>
  cancelForm.suspendListing
    ? VISIT_CANCEL_SUSPEND_SUCCESS_TITLE
    : VISIT_CANCEL_SUCCESS_TITLE
);
const cancelSuccessMessage = computed(() =>
  cancelForm.suspendListing
    ? VISIT_CANCEL_SUSPEND_SUCCESS_MESSAGE
    : VISIT_CANCEL_SUCCESS_MESSAGE
);
const canSuspendListing = computed(
  () =>
    capabilityFor('suspend_listing').allowed &&
    currentAccount.value?.features?.listing_suspension !== false &&
    currentAccount.value?.settings?.can_suspend_listing !== false
);
const cancelReasonHelperMessage = computed(() =>
  getCancelMessage(cancelForm.reason, {
    canSuspendListing: canSuspendListing.value,
  })
);

const validateSuspensionPeriod = () => {
  if (!cancelForm.suspendListing) return true;

  if (!cancelForm.suspensionStartsAt || !cancelForm.suspensionEndsAt) {
    useAlert(VISIT_CANCEL_INVALID_DATE_MESSAGE);
    return false;
  }

  const startsAt = new Date(cancelForm.suspensionStartsAt);
  const endsAt = new Date(cancelForm.suspensionEndsAt);

  if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
    useAlert(VISIT_CANCEL_INVALID_DATE_MESSAGE);
    return false;
  }

  if (startsAt > endsAt) {
    useAlert(VISIT_CANCEL_INVALID_FIRST_DAY_MESSAGE);
    return false;
  }

  return true;
};

const confirmarSelecionadas = async () => {
  if (!canConfirmVisits.value) {
    useAlert('Você não possui permissão para confirmar visitas.');
    return;
  }

  const ids = Array.from(selected.value);
  if (selectedHasExpiredVisit.value) {
    useAlert(VISIT_CONFIRM_EXPIRED_MESSAGE);
    return;
  }

  isConfirmingSelected.value = true;
  try {
    const results = await Promise.allSettled(
      ids.map(id => store.update({ id, status: 'confirmada' }))
    );
    const { confirmedIds, failedIds, errors } = summarizeBatchVisitConfirmation(
      ids,
      results
    );

    if (confirmedIds.length) {
      useAlert(
        `${confirmedIds.length} ${
          confirmedIds.length === 1
            ? 'visita confirmada'
            : 'visitas confirmadas'
        }`
      );
    }

    selected.value = new Set(failedIds);
    if (failedIds.length) {
      const details = errors.length ? ` ${errors.join(' ')}` : '';
      useAlert(
        `${failedIds.length} ${
          failedIds.length === 1
            ? 'visita não foi confirmada.'
            : 'visitas não foram confirmadas.'
        }${details}`
      );
    }

    await fetchVisitas();
  } finally {
    isConfirmingSelected.value = false;
  }
};

useEscClose(showCancelModal, () => {
  showCancelModal.value = false;
});
useEscClose(showAvailabilityModal, () => {
  showAvailabilityModal.value = false;
});

async function loadAvailabilityOptions() {
  if (!availabilityVisit.value?.imovel?.id) return;

  isLoadingAvailability.value = true;
  availabilityError.value = '';
  showAnticipationUnavailable.value = false;
  anticipationUnavailableReason.value = '';
  try {
    const payload = await store.availability({
      imovel_id: availabilityVisit.value.imovel.id,
      user_id:
        availabilityVisit.value.user?.id || availabilityVisit.value.userId,
      contact_id:
        availabilityVisit.value.contact?.id ||
        availabilityVisit.value.contactId,
      exclude_visita_id: availabilityVisit.value.id,
      days: 7,
    });
    availabilityDays.value = (payload.availability || []).map(day => ({
      ...day,
      slots: (day.slots || []).map(slot => ({
        ...slot,
        selected: slot.is_selected !== false,
      })),
    }));
  } catch (e) {
    availabilityDays.value = [];
    availabilityError.value =
      'Houve algum problema ao carregar os dados, favor tente mais tarde';
  } finally {
    isLoadingAvailability.value = false;
  }
}

function openAvailabilityModal(visita) {
  if (!canManageAvailability.value) return;
  if (!visita?.imovel?.id) return;

  availabilityVisit.value = visita;
  showAvailabilityModal.value = true;
  selectedAvailabilityDay.value = 0;
  showAnticipationUnavailable.value = false;
  anticipationUnavailableReason.value = '';
  loadAvailabilityOptions();
}

function availabilityDateId(day) {
  return day?.date_id || day?.dateId || '';
}

function availabilityLabel(day) {
  const dateId = availabilityDateId(day);
  if (!dateId) return day?.label || 'Dia';

  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
  }).format(new Date(`${dateId}T00:00:00Z`));
}

function availabilityWeekDayKey(dateId) {
  const date = new Date(`${dateId}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';

  return String(date.getUTCDay());
}

function slotTimeId(slot) {
  return slot?.time_id || slot?.timeId || '';
}

function slotHighDemand(slot) {
  return slot?.is_high_demand || slot?.isHighDemand;
}

function availabilitySlotReason(slot) {
  return slot?.unavailable_reason || slot?.unavailableReason || '';
}

function availabilitySlotDisabled(slot) {
  const reason = availabilitySlotReason(slot);
  return !!reason && reason !== 'property_unavailable';
}

function availabilitySlotStatus(slot) {
  const reason = availabilitySlotReason(slot);
  if (!reason || reason === 'property_unavailable') return '';

  if (reason === 'user_conflict') return 'Corretor indisponível';
  if (reason === 'contact_conflict') return 'Cliente indisponível';
  if (reason === 'imovel_conflict') return 'Imóvel já tem visita';
  if (reason === 'outside_business_hours') return 'Fora do horário';
  return 'Indisponível';
}

function openAnticipationUnavailable(slot) {
  anticipationUnavailableReason.value =
    availabilitySlotReason(slot) || 'no_slots';
  showAnticipationUnavailable.value = true;
  trackVisitFlowEvent(
    'visit_anticipation_unavailable_shown',
    visitFlowTrackingPayload(availabilityVisit.value, {
      unavailable_reason: anticipationUnavailableReason.value,
    })
  );
}

const toggleAvailabilitySlot = slot => {
  if (!canManageAvailability.value) return;

  if (availabilitySlotDisabled(slot)) {
    openAnticipationUnavailable(slot);
    return;
  }

  slot.selected = !slot.selected;
};

const trackAvailabilityWarning = payload => {
  trackReferenceEvent(
    AGENDA_SATELLITE_TRACKING_EVENTS.DATETIME_WITH_WARNING_SELECTED,
    {
      warning: payload?.warning || 'high_demand',
      time: payload?.time,
    }
  );
};

const selectedSlotsCount = computed(() =>
  availabilityDays.value.reduce(
    (total, day) =>
      total +
      (day.slots || []).filter(slot => slot.selected && slotTimeId(slot))
        .length,
    0
  )
);

const goToAvailabilityListing = () => {
  const imovelId = availabilityVisit.value?.imovel?.id;
  if (!imovelId) return;

  trackVisitFlowEvent('visit_anticipation_configure_hours_clicked', {
    imovel_id: imovelId,
  });
  showAvailabilityModal.value = false;
  showCancelModal.value = false;
  router.push(
    frontendURL(`accounts/${accountId.value}/imoveis/${imovelId}/edit`)
  );
};

const businessContextForVisit = visita =>
  visita?.imovel?.finalidade === 'venda' ? 'sale' : 'rental';

const openBlockedSheet = visita => {
  if (!visita?.id) return;

  blockedVisit.value = visita;
  blockedNotifyLoading.value = false;
  blockedNotifyDone.value = false;
  blockedNotifyError.value = '';
  showQuickActionsDrawer.value = false;
  showBlockedSheet.value = true;
  trackVisitFlowEvent(
    'visiting_blocked_sheet_shown',
    visitFlowTrackingPayload(visita, {
      block_reason: rawBlockedReason(visita) || 'unknown',
    })
  );
};

const closeBlockedSheet = () => {
  showBlockedSheet.value = false;
};

const notifyBlockedAvailability = async () => {
  if (!canManageAvailability.value) return;

  const imovelId =
    propertyId(blockedVisit.value) || blockedVisit.value?.imovel?.id;
  if (!imovelId) {
    blockedNotifyError.value = 'Imóvel não informado para registrar o aviso.';
    return;
  }

  blockedNotifyLoading.value = true;
  blockedNotifyError.value = '';
  trackVisitFlowEvent('visiting_blocked_notify_clicked', {
    imovel_id: imovelId,
  });
  try {
    await ImovelAPI.notifyAvailability(imovelId, {
      visit_id: blockedVisit.value?.id,
      source: 'visiting_blocked',
    });
    blockedNotifyDone.value = true;
    useAlert('Aviso registrado. Vamos avisar quando houver horário.');
  } catch {
    blockedNotifyError.value =
      'Não foi possível registrar o aviso. Tente novamente.';
  } finally {
    blockedNotifyLoading.value = false;
  }
};

const goToBlockedSimilar = () => {
  const imovelId =
    propertyId(blockedVisit.value) || blockedVisit.value?.imovel?.id;
  if (!imovelId) return;

  trackVisitFlowEvent('visiting_blocked_similar_clicked', {
    imovel_id: imovelId,
  });
  closeBlockedSheet();
  router.push({
    name: 'imoveis_index',
    params: { accountId: accountId.value },
    query: {
      similar_to: imovelId,
      business_context: businessContextForVisit(blockedVisit.value),
    },
  });
};

const keepCurrentVisitAfterAnticipation = () => {
  showAnticipationUnavailable.value = false;
};

const saveAvailabilityOptions = async () => {
  if (!canManageAvailability.value) return;
  if (!availabilityVisit.value?.imovel?.id) return;

  if (!selectedSlotsCount.value) {
    useAlert('Selecione ao menos 1 opção');
    return;
  }

  isSavingAvailability.value = true;
  try {
    const availabilityWeekDays = availabilityDays.value.reduce((acc, day) => {
      const weekdayKey = availabilityWeekDayKey(availabilityDateId(day));
      if (!weekdayKey) return acc;

      acc[weekdayKey] = (day.slots || [])
        .filter(slot => slot.selected)
        .map(slotTimeId)
        .filter(Boolean);
      return acc;
    }, {});

    await store.updateAvailability({
      imovel_id: availabilityVisit.value.imovel.id,
      availability_week_days: availabilityWeekDays,
    });
    useAlert('Horários para visitas atualizados com sucesso.');
    showAvailabilityModal.value = false;
    showCancelModal.value = false;
    await fetchVisitas();
  } catch (e) {
    useAlert(
      'Não foi possível atualizar os horários para visitas. Tente novamente.'
    );
  } finally {
    isSavingAvailability.value = false;
  }
};

function buildCancellationAttributes() {
  return {
    visit_cancellation: {
      title: VISIT_CANCEL_DETAILS_TITLE,
      responsible: cancelForm.responsible,
      responsible_label: cancelResponsibleLabel(cancelForm.responsible),
      reason: cancelForm.reason,
      reason_message: cancelReasonLabel(
        cancelForm.responsible,
        cancelForm.reason
      ),
      reason_helper_message: cancelReasonHelperMessage.value || null,
      notes: cancelForm.notes?.trim() || null,
      confirm_title: VISIT_CANCEL_CONFIRM_TITLE,
      confirm_message: VISIT_CANCELLATION_NOTICE,
      confirm_label: cancelConfirmLabel.value,
      reschedule_suggested: !!selectedVisit.value,
      visit_summary: selectedVisitSummary.value,
      suspend_listing: cancelForm.suspendListing,
      suspension_period: cancelForm.suspendListing
        ? {
            starts_at: cancelForm.suspensionStartsAt,
            ends_at: cancelForm.suspensionEndsAt,
            label: VISIT_CANCEL_SUSPEND_LABEL,
            helper: VISIT_CANCEL_SUSPEND_HELPER,
          }
        : null,
      republish_message: suspensionAlert.value || null,
      success_title: cancelSuccessTitle.value,
      success_message: cancelSuccessMessage.value,
      canceled_at: new Date().toISOString(),
    },
  };
}

const selectedHasUncancelableVisit = computed(() =>
  selectedVisits.value.some(visita => !canCancelVisitAction(visita))
);
const selectedCancelableIds = computed(() =>
  selectedVisits.value
    .filter(visita => canCancelVisitAction(visita))
    .map(visita => visita.id)
);

const cancelarSelecionadas = async () => {
  if (!canCancelVisits.value) {
    useAlert('Você não possui permissão para cancelar visitas.');
    return;
  }

  const ids = selectedCancelableIds.value;
  if (!ids.length) return;
  if (selectedHasExpiredVisit.value) {
    useAlert(VISIT_CANCEL_EXPIRED_MESSAGE);
    return;
  }
  if (selectedHasUncancelableVisit.value) {
    useAlert('Selecione apenas visitas em aberto para cancelar.');
    return;
  }
  if (!cancelForm.reason) {
    useAlert('Informe o motivo de cancelamento da visita');
    return;
  }
  if (!validateSuspensionPeriod()) return;

  const additionalAttributes = buildCancellationAttributes();
  await store.batchCancel(ids, {
    status: 'cancelada',
    additional_attributes: additionalAttributes,
  });
  useAlert(cancelSuccessTitle.value);
  trackReferenceEvent('agenda_visit_cancellation_confirmed', {
    selected_count: ids.length,
    reason: cancelForm.reason,
    responsible: cancelForm.responsible,
    suspend_listing: cancelForm.suspendListing,
    has_notes: Boolean(cancelForm.notes?.trim()),
    has_suspension_period: Boolean(cancelForm.suspendListing),
  });
  selected.value.clear();
  showCancelModal.value = false;
  fetchVisitas();
};

const allSelected = computed(
  () =>
    visitas.value.length > 0 &&
    visitas.value.every(v => selected.value.has(v.id))
);
const toggleAll = () => {
  if (allSelected.value) selected.value.clear();
  else visitas.value.forEach(v => selected.value.add(v.id));
};
const toggleOne = id =>
  selected.value.has(id) ? selected.value.delete(id) : selected.value.add(id);

const goToPreviousPage = () => {
  if (page.value <= 1) return;

  trackReferenceEvent(SCHEDULED_VISITS_TRACKING_EVENTS.PREVIOUS_PAGE, {
    page: page.value - 1,
  });
  page.value -= 1;
  selected.value.clear();
  fetchVisitas();
};

const goToNextPage = () => {
  if (page.value >= totalPages.value) return;

  trackReferenceEvent(SCHEDULED_VISITS_TRACKING_EVENTS.NEXT_PAGE, {
    page: page.value + 1,
  });
  page.value += 1;
  selected.value.clear();
  fetchVisitas();
};

const changePerPage = value => {
  const nextValue = Number(value);
  if (!Number.isFinite(nextValue) || nextValue <= 0) return;
  perPage.value = nextValue;
  page.value = 1;
  applyFilters('per_page');
};

const statusLabel = visita =>
  VISITA_CARD_STATUS_LABELS[cardStatus(visita)] ||
  visitSummary(visita).statusLabel ||
  getVisitaStatusLabel(visita.status);

const statusClass = visita =>
  VISITA_CARD_STATUS_CLASSES[cardStatus(visita)] ||
  getVisitaStatusClass(visita.status);

const cardStatusVariant = visita => {
  const status = cardStatus(visita);
  if (
    [VISITA_CARD_STATUS.DONE, VISITA_CARD_STATUS.CONFIRMED].includes(status)
  ) {
    return 'success';
  }
  if (
    [
      VISITA_CARD_STATUS.PENDING,
      VISITA_CARD_STATUS.WAITING_CONFIRMATION,
      VISITA_CARD_STATUS.UNSUCCESSFUL,
    ].includes(status)
  ) {
    return 'attention';
  }
  if (
    [VISITA_CARD_STATUS.CANCELED, VISITA_CARD_STATUS.NOT_HAPPENED].includes(
      status
    )
  ) {
    return 'error';
  }
  if (status === VISITA_CARD_STATUS.UNAVAILABLE_HOURS) return 'attention';
  if (status === VISITA_CARD_STATUS.SCHEDULED) return 'info';
  return 'new';
};
const shouldShowRescheduleCTA = visita =>
  canRescheduleVisitAction(visita) &&
  (isVisitBlocked(visita) ||
    cardStatus(visita) === VISITA_CARD_STATUS.UNAVAILABLE_HOURS);
const shouldShowFinishCTA = visita =>
  canFinishVisit(visita) &&
  (!visitTime(visita) || visitTime(visita) <= Date.now()) &&
  [
    VISITA_CARD_STATUS.NOW,
    VISITA_CARD_STATUS.PENDING,
    VISITA_CARD_STATUS.CONFIRMED,
    VISITA_CARD_STATUS.SCHEDULED,
  ].includes(cardStatus(visita));
const shouldShowOfferCTA = visita =>
  canSendVisitOffer.value && cardStatus(visita) === VISITA_CARD_STATUS.DONE;
const isRichDoneVisit = visita =>
  [
    VISITA_CARD_STATUS.DONE,
    VISITA_CARD_STATUS.NOT_HAPPENED,
    VISITA_CARD_STATUS.UNSUCCESSFUL,
  ].includes(cardStatus(visita));
const isStalledVisit = visita =>
  ['stalled', 'paralisada'].includes(
    normalizedToken(
      firstPresent(visita?.status_v3, visitValue(visita, 'flowStatus'))
    )
  );
const shouldShowPendingFinish = visita =>
  cardStatus(visita) === VISITA_CARD_STATUS.PENDING;
const quickActionMatrixFor = visita => ({
  confirm: {
    action: 'confirm_visit',
    allowed: shouldShowConfirmCTA(visita),
  },
  cancel: {
    action: 'cancel_visit',
    allowed: canCancelVisitAction(visita),
  },
  reschedule: {
    action: 'reschedule_visit',
    allowed: canRescheduleVisitAction(visita),
  },
  followUp: {
    action: 'finish_visit',
    allowed: canFollowUpVisitAction(visita),
  },
  addMore: {
    action: 'add_more_visits',
    allowed: canAddMoreVisitsAction(visita),
  },
});
const activeQuickActionMatrix = computed(() =>
  quickActionMatrixFor(activeQuickVisit.value)
);
const unreadCountForVisit = visita =>
  Number(
    firstPresent(
      visitValue(visita, 'unreadCount', 'unread_count'),
      visita?.conversation?.unreadCount,
      visita?.conversation?.unread_count,
      0
    )
  ) || 0;
const unreadMessageVisit = computed(() =>
  visitas.value.find(visita => unreadCountForVisit(visita) > 0)
);
const unreadSnackbarTrackingKey = ref('');
const hasQualifiedVisitor = computed(() =>
  visitas.value.some(visita => isQualifiedVisitor(visita))
);
const showAgentActivityAlert = computed(
  () =>
    (isOperationalList.value && visitBucket.value === 'upcoming') ||
    isAgendaView.value
);
const sortedVisitsForVisual = computed(() =>
  [...visitas.value].sort((a, b) =>
    sortOrder.value.includes('desc')
      ? visitTime(b) - visitTime(a)
      : visitTime(a) - visitTime(b)
  )
);
const visitDateKey = visita => {
  const value = visitDateValue(visita);
  return value ? localDateKey(value) : '';
};
const activeAgendaVisits = computed(() =>
  sortedVisitsForVisual.value.filter(
    visita => visitDateKey(visita) === activeAgendaDateKey.value
  )
);

// --- Presentation-only summaries for the redesign metric strips.
// These are read-only derivations from existing reactive state and helpers.
// No data loading, store, API call, permission gate or existing feature is
// changed here — they only reshape data already on screen into top-of-page
// tiles (LaisMetricStrip), mirroring the Imóveis/Leads redesign wave.
const countVisitsByStatus = (list, statuses) =>
  list.filter(visita => statuses.includes(cardStatus(visita))).length;

const agendaDayMetrics = computed(() => {
  const list = activeAgendaVisits.value;
  const scheduled = countVisitsByStatus(list, [
    VISITA_CARD_STATUS.NOW,
    VISITA_CARD_STATUS.PENDING,
    VISITA_CARD_STATUS.WAITING_CONFIRMATION,
    VISITA_CARD_STATUS.CONFIRMED,
    VISITA_CARD_STATUS.SCHEDULED,
    VISITA_CARD_STATUS.UNAVAILABLE_HOURS,
  ]);
  const done = countVisitsByStatus(list, [VISITA_CARD_STATUS.DONE]);
  const noShow = countVisitsByStatus(list, [
    VISITA_CARD_STATUS.NOT_HAPPENED,
    VISITA_CARD_STATUS.UNSUCCESSFUL,
  ]);
  return [
    {
      id: 'total',
      label: isActiveAgendaDateToday.value ? 'Visitas hoje' : 'Visitas no dia',
      value: list.length,
      helper: activeAgendaDayLabel.value,
      icon: 'i-lucide-calendar-days',
      tone: 'info',
    },
    {
      id: 'scheduled',
      label: 'Agendadas',
      value: scheduled,
      helper: 'Aguardando ou confirmadas',
      icon: 'i-lucide-clock',
      tone: scheduled ? 'info' : 'neutral',
    },
    {
      id: 'done',
      label: 'Concluídas',
      value: done,
      helper: 'Visitas realizadas',
      icon: 'i-lucide-circle-check',
      tone: done ? 'positive' : 'neutral',
    },
    {
      id: 'no_show',
      label: 'Não compareceu',
      value: noShow,
      helper: 'Sem sucesso ou ausência',
      icon: 'i-lucide-user-x',
      tone: noShow ? 'warning' : 'neutral',
    },
  ];
});

const blockedOnPageCount = computed(
  () => visitas.value.filter(visita => isVisitBlocked(visita)).length
);

const visitListMetrics = computed(() => [
  {
    id: 'total',
    label: 'Total no filtro',
    value: totalCount.value,
    helper: currentVisitBucket.value.label,
    icon: 'i-lucide-calendar-check',
    tone: 'info',
  },
  {
    id: 'loaded',
    label: 'Nesta página',
    value: visitas.value.length,
    helper: `Página ${page.value} de ${totalPages.value}`,
    icon: 'i-lucide-layout-list',
    tone: 'neutral',
  },
  {
    id: 'selected',
    label: 'Selecionadas',
    value: selected.value.size,
    helper: 'Para ação em lote',
    icon: 'i-lucide-check-check',
    tone: selected.value.size ? 'info' : 'neutral',
  },
  {
    id: 'blocked',
    label: 'Reserva bloqueada',
    value: blockedOnPageCount.value,
    helper: 'Precisam de novo horário',
    icon: 'i-lucide-calendar-x-2',
    tone: blockedOnPageCount.value ? 'warning' : 'neutral',
  },
]);
const visitDayLabel = visita => {
  const date = new Date(visitDateValue(visita) || 0);
  if (Number.isNaN(date.getTime())) return 'Sem data definida';

  return new Intl.DateTimeFormat('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
  }).format(date);
};
const groupedVisitsByDay = computed(() => {
  const groups = [];
  const byLabel = new Map();

  sortedVisitsForVisual.value.forEach(visita => {
    const label = visitDayLabel(visita);
    if (!byLabel.has(label)) {
      const group = { label, visits: [] };
      byLabel.set(label, group);
      groups.push(group);
    }
    byLabel.get(label).visits.push(visita);
  });

  return groups;
});
const scheduledClientCards = computed(() => {
  return buildScheduledClientCards({
    visits: sortedVisitsForVisual.value,
    orderBy: scheduledClientsOrder.value,
    getContactPhone: contactPhone,
  });
});

const showScheduledClientsSection = computed(
  () => isOperationalList.value && visitBucket.value === 'upcoming'
);

const scheduledClientsSortEvents = {
  [SCHEDULED_VISITS_ORDER_BY_QUERY.CLOSER_VISITS]:
    SCHEDULED_VISITS_TRACKING_EVENTS.SORT_BY_CLOSEST_VISITS,
  [SCHEDULED_VISITS_ORDER_BY_QUERY.NEW_CLIENTS]:
    SCHEDULED_VISITS_TRACKING_EVENTS.SORT_BY_NEW_CLIENTS,
  [SCHEDULED_VISITS_ORDER_BY_QUERY.NAME]:
    SCHEDULED_VISITS_TRACKING_EVENTS.SORT_BY_ALPHABETICAL,
};

const selectScheduledClientsOrder = value => {
  scheduledClientsOrder.value = value;
  trackReferenceEvent(SCHEDULED_VISITS_TRACKING_EVENTS.SORTING_LIST_CLICKED, {
    order_by: value,
  });
  trackReferenceEvent(
    scheduledClientsSortEvents[value] ||
      SCHEDULED_VISITS_TRACKING_EVENTS.SORT_BY_CLOSEST_VISITS,
    {
      order_by: value,
    }
  );
};

const onScheduledClientToggle = (client, expanded) => {
  if (!expanded) return;
  trackReferenceEvent(SCHEDULED_VISITS_TRACKING_EVENTS.EXPAND_CARD, {
    ...visitFlowTrackingPayload(client.visit),
    contact_id: client.id,
  });
};

const listingContext = visita => {
  const summaryLabel = visitSummary(visita).listingContextLabel;
  if (summaryLabel) return summaryLabel;

  const finalidade = visita.imovel?.finalidade;
  if (finalidade === 'aluguel') return 'Aluguel';
  if (finalidade === 'venda') return 'Venda';
  if (finalidade === 'temporada') return 'Temporada';
  return 'Anúncio';
};

const entryAuthorization = visita =>
  visitSummary(visita).entryAuthorizationLabel ||
  entryAccessLabel(
    visita.additionalAttributes?.entryAccess?.accessLabel ||
      visita.additionalAttributes?.entry_access?.access_label ||
      visita.additionalAttributes?.entryAccess?.accessKey ||
      visita.additionalAttributes?.entry_access?.access_key ||
      visita.additionalAttributes?.entry_authorization ||
      visita.additionalAttributes?.entrada_no_imovel ||
      visita.additionalAttributes?.entryAuthorization
  );

const visitIntention = visita =>
  visitSummary(visita).visitIntentionLabel ||
  visitIntentionLabel(
    visita.buyerVisitIntention || visita.buyer_visit_intention || 'indefinido'
  );

const cancellationDetails = visita =>
  visita.additionalAttributes?.visit_cancellation ||
  visita.additionalAttributes?.visitCancellation ||
  null;

const cancellationReason = visita =>
  visitCancellationSummary(visita)?.reasonLabel ||
  cancellationDetails(visita)?.reason_message ||
  cancellationDetails(visita)?.reasonMessage ||
  '';

const cancelReasonOptions = computed(() =>
  getCancelReasonOptions(cancelForm.responsible, {
    canSuspendListing: canSuspendListing.value,
  })
);

const setDefaultCancelReason = () => {
  cancelForm.reason = cancelReasonOptions.value[0]?.value || '';
};

const updateCancelResponsible = () => {
  setDefaultCancelReason();
};

const resetCancelForm = () => {
  cancelForm.responsible = 'demand';
  cancelForm.reason = 'client_stopped_responding';
  cancelForm.notes = '';
  cancelForm.suspendListing = false;
  cancelForm.suspensionStartsAt = isoDate(new Date());
  cancelForm.suspensionEndsAt = addDays(7);
};

const openCancelModal = () => {
  if (!canCancelVisits.value) {
    useAlert('Você não possui permissão para cancelar visitas.');
    return;
  }

  if (selectedHasExpiredVisit.value) {
    useAlert(VISIT_CANCEL_EXPIRED_MESSAGE);
    return;
  }
  if (selectedHasUncancelableVisit.value) {
    useAlert('Selecione apenas visitas em aberto para cancelar.');
    return;
  }
  resetCancelForm();
  showCancelModal.value = true;
  trackReferenceEvent('visit_cancel_page_viewed', {
    selected_count: selectedCancelableIds.value.length,
    has_suspension_period: false,
  });
};

const continueQuickCancellation = () => {
  const visit = activeQuickVisit.value;
  const visitId = visit?.id;
  if (!visitId) return;
  if (!canCancelVisitAction(visit)) return;

  trackReferenceEvent(VISIT_TRACKING_EVENTS.CANCEL_CONTINUE_CLICKED, {
    ...visitFlowTrackingPayload(visit),
    action_origin: 'visitas_index_drawer',
  });

  selected.value = new Set([visitId]);
  showWantToCancelModal.value = false;
  closeQuickActions();
  openCancelModal();
};

const openUnreadMessageVisit = visit => {
  if (!visit) return;

  const payload = {
    ...visitFlowTrackingPayload(visit),
    unread_count: unreadCountForVisit(visit),
  };
  trackReferenceEvent('unread_messages_snackbar_open_clicked', payload);
  trackReferenceEvent('chatcrpp_access_allchats_clicked', payload);
  openVisitMessage(visit);
};

const actions = computed(() =>
  [
    {
      capability: 'confirm',
      label: 'Confirmar selecionadas',
      icon: 'i-lucide-circle-check',
      disabled:
        selected.value.size === 0 ||
        !canConfirmVisits.value ||
        isConfirmingSelected.value,
      onClick: confirmarSelecionadas,
    },
    {
      capability: 'cancel',
      label: 'Cancelar selecionadas',
      icon: 'i-lucide-circle-x',
      disabled:
        selected.value.size === 0 ||
        selectedCancelableIds.value.length === 0 ||
        selectedHasUncancelableVisit.value ||
        !canCancelVisits.value,
      onClick: openCancelModal,
    },
  ].filter(action => capabilityFor(action.capability).visible)
);

onMounted(() => {
  if (isOperationalList.value) fetchVisitas();
  else if (isAgendaView.value && !hasOperationalPanelSection.value)
    fetchAgendaVisits();
});

watch(
  () => [
    route.name,
    route.path,
    route.query.tab,
    route.query.day,
    route.query.view,
    route.query.business_context,
    route.query.businessContext,
    route.query.section,
    route.query.visit_bucket,
    route.query.visit_type,
    route.query.status,
    route.query.contact_query,
    route.query.imovel_query,
    route.query.user_query,
    route.query.date,
    route.query.start_date,
    route.query.end_date,
    route.query.sorted_by,
    route.query.sortedBy,
    route.query.order_by,
    route.query.orderBy,
    route.query.visit_id,
    route.query.visita_id,
    route.query.scheduling_id,
    route.query.open_visit,
    route.query.page,
    route.query.per_page,
  ],
  async () => {
    if (isSyncingRouteState) return;

    applyRouteState();
    if (isOperationalList.value) {
      await fetchVisitas({ updateRoute: false });
    } else if (isAgendaView.value && !hasOperationalPanelSection.value) {
      await fetchAgendaVisits({ updateRoute: false });
    } else {
      showFilters.value = false;
      loadError.value = false;
    }
  }
);

watch(unreadMessageVisit, visit => {
  if (!visit) {
    unreadSnackbarTrackingKey.value = '';
    return;
  }

  const trackingKey = `${visit.id || visit.visit_id}:${unreadCountForVisit(visit)}`;
  if (trackingKey === unreadSnackbarTrackingKey.value) return;

  unreadSnackbarTrackingKey.value = trackingKey;
  trackReferenceEvent('unread_messages_snackbar_viewed', {
    ...visitFlowTrackingPayload(visit),
    unread_count: unreadCountForVisit(visit),
  });
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="flex h-full min-h-0 flex-col overflow-y-auto bg-white font-redHatText md:overflow-hidden"
  >
    <LaisHeader :title="headerTitle" :helper="headerHelper" sticky>
      <template #actions>
        <div
          v-if="isAdmin"
          v-on-clickaway="() => (showAutomationMenu = false)"
          class="relative"
        >
          <LaisPrimaryButton
            icon="i-lucide-workflow"
            variant="ghost"
            aria-haspopup="menu"
            :aria-expanded="showAutomationMenu ? 'true' : 'false'"
            @click="showAutomationMenu = !showAutomationMenu"
          >
            {{ t('AUTOMATION.HEADER_BTN_TXT') }}
          </LaisPrimaryButton>
          <DropdownMenu
            v-if="showAutomationMenu"
            :menu-items="automationMenuItems"
            class="right-0 top-full mt-1 max-h-80 w-64 overflow-y-auto"
            @action="handleAutomationMenuAction"
          />
        </div>
        <LaisFilterButton
          v-if="isOperationalList && canUseVisitFilters"
          :active="hasActiveFilters"
          @click="showFilters = !showFilters"
        />
        <LaisPrimaryButton
          v-if="isOperationalList && canCreateVisit"
          icon="i-lucide-plus"
          variant="primary"
          :disabled="!canCreateVisitAction"
          @click="novaVisita"
        >
          Nova visita
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <VisitsAgendaTabs
      v-if="isAgendaView"
      :dates="agendaDates"
      :active-index="agendaActiveIndex"
      @select="selectAgendaDay"
    />

    <VisitsBucketTabs
      v-if="isOperationalList"
      :buckets="visitBuckets"
      :active-bucket="visitBucket"
      aria-label="Abas de visitas"
      :tab-id-for="visitBucketTabId"
      :panel-id-for="visitBucketPanelId"
      @select="selectVisitBucket"
      @keydown="handleVisitBucketKeydown"
    />

    <div
      v-if="isOperationalList"
      class="border-b border-lais-border bg-white px-4 py-3 sm:px-6 lg:px-10"
    >
      <div class="flex items-center gap-2 overflow-x-auto no-scrollbar">
        <button
          v-for="item in visitTypeFilters"
          :key="item.value"
          type="button"
          class="rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-colors whitespace-nowrap"
          :class="
            visitType === item.value
              ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
              : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
          "
          @click="selectVisitType(item.value)"
        >
          {{ item.label }}
        </button>
      </div>
    </div>

    <LaisFilterBar
      v-if="isOperationalList && canUseVisitFilters"
      :show="showFilters"
    >
      <div class="flex min-w-[150px] flex-1 flex-col gap-1 sm:max-w-[180px]">
        <label
          class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
        >
          Status
        </label>
        <select
          v-model="statusFilter"
          class="h-10 w-full px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text focus:outline-none focus:border-lais-primary"
          @change="applyFilters('status')"
        >
          <option value="todos">Todos status</option>
          <option value="agendada">Visita agendada</option>
          <option value="confirmada">Visita confirmada</option>
          <option value="realizada">Visita realizada</option>
          <option value="cancelada">Visita cancelada</option>
          <option value="nao_compareceu">Visita não realizada</option>
        </select>
      </div>
      <div class="flex min-w-[180px] flex-[1.2] flex-col gap-1">
        <label
          class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
        >
          Cliente
        </label>
        <input
          v-model="contactQuery"
          type="search"
          placeholder="Nome, e-mail ou telefone"
          class="h-10 w-full px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text placeholder:text-lais-text-muted focus:outline-none focus:border-lais-primary"
          @keyup.enter="applyFilters('contact_query')"
          @blur="applyFilters('contact_query')"
        />
      </div>
      <div class="flex min-w-[180px] flex-[1.2] flex-col gap-1">
        <label
          class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
        >
          Imóvel
        </label>
        <input
          v-model="imovelQuery"
          type="search"
          placeholder="Código, rua, bairro, cidade ou endereço completo"
          class="h-10 w-full px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text placeholder:text-lais-text-muted focus:outline-none focus:border-lais-primary"
          @keyup.enter="applyFilters('imovel_query')"
          @blur="applyFilters('imovel_query')"
        />
      </div>
      <div class="flex min-w-[160px] flex-1 flex-col gap-1">
        <label
          class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
        >
          Corretor
        </label>
        <input
          v-model="userQuery"
          type="search"
          placeholder="Nome ou e-mail"
          class="h-10 w-full px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text placeholder:text-lais-text-muted focus:outline-none focus:border-lais-primary"
          @keyup.enter="applyFilters('user_query')"
          @blur="applyFilters('user_query')"
        />
      </div>
      <div class="flex min-w-[150px] flex-1 flex-col gap-1 sm:max-w-[170px]">
        <label
          class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
        >
          Data exata
        </label>
        <input
          v-model="exactDate"
          type="date"
          class="h-10 w-full px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text focus:outline-none focus:border-lais-primary"
          @change="applyFilters('exact_date')"
        />
      </div>
      <div class="flex min-w-[150px] flex-1 flex-col gap-1 sm:max-w-[170px]">
        <label
          class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
        >
          De
        </label>
        <input
          v-model="startDate"
          type="date"
          class="h-10 w-full px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text focus:outline-none focus:border-lais-primary"
          @change="applyFilters('start_date')"
        />
      </div>
      <div class="flex min-w-[150px] flex-1 flex-col gap-1 sm:max-w-[170px]">
        <label
          class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
        >
          Até
        </label>
        <input
          v-model="endDate"
          type="date"
          class="h-10 w-full px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text focus:outline-none focus:border-lais-primary"
          @change="applyFilters('end_date')"
        />
      </div>
      <div class="flex min-w-[160px] flex-1 flex-col gap-1 sm:max-w-[180px]">
        <label
          class="text-[11px] font-semibold text-lais-text-muted uppercase tracking-wide"
        >
          Classificar por
        </label>
        <select
          v-model="sortOrder"
          class="h-10 w-full px-3 bg-white border border-lais-border rounded-lais text-[14px] text-lais-text focus:outline-none focus:border-lais-primary"
          @change="selectSortOrder"
        >
          <option value="visitTime,desc">Mais recentes</option>
          <option value="visitTime,asc">Mais antigas</option>
        </select>
      </div>
      <button
        type="button"
        class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[13px] font-semibold text-lais-text transition-colors hover:border-lais-primary hover:text-lais-primary"
        @click="clearFilters"
      >
        Limpar
      </button>
    </LaisFilterBar>

    <div
      v-if="visitsCapabilityNotice"
      class="mx-4 mt-4 rounded-lais-sm border px-4 py-3 text-[13px] leading-relaxed sm:mx-6 lg:mx-10"
      :class="
        visitsCapabilityNotice.tone === 'danger'
          ? 'border-lais-danger/40 bg-lais-danger-50 text-lais-danger'
          : 'border-amber-200 bg-amber-50 text-amber-800'
      "
    >
      <p class="font-semibold">{{ visitsCapabilityNotice.title }}</p>
      <p class="mt-1">{{ visitsCapabilityNotice.description }}</p>
    </div>

    <div
      v-if="capabilitiesContext.state.permissionDenied"
      class="mx-4 mt-4 rounded-lais border border-lais-border bg-lais-bg-soft px-6 py-12 text-center sm:mx-6 lg:mx-10"
    >
      <span
        class="i-lucide-lock mx-auto block size-8 text-lais-text-empty"
        aria-hidden="true"
      />
      <p class="mt-3 text-[14px] font-semibold text-lais-text-body">
        Acesso restrito
      </p>
      <p class="mt-1 text-[12px] text-lais-text-muted">
        Seu perfil não possui permissão para visualizar visitas nesta conta.
      </p>
    </div>

    <div
      v-else-if="isOperationalList"
      :id="visitBucketPanelId(visitBucket)"
      class="flex min-h-0 flex-1 flex-col max-md:flex-none"
      role="tabpanel"
      :aria-labelledby="visitBucketTabId(visitBucket)"
      :aria-busy="isLoading ? 'true' : 'false'"
      aria-live="polite"
    >
      <div class="grid gap-3 px-4 py-4 sm:px-6 lg:px-10">
        <LaisMetricStrip
          :items="visitListMetrics"
          :loading="isLoading && visitas.length === 0"
          aria-label="Resumo das visitas no filtro atual"
        />
        <div
          class="flex flex-wrap items-center justify-between gap-x-4 gap-y-1"
        >
          <p class="text-[13px]">
            <span
              class="text-lais-primary underline decoration-lais-gold decoration-[1.5px] underline-offset-[3px]"
            >
              Exibindo {{ totalCount }}
              {{ totalCount === 1 ? 'visita' : 'visitas' }}
            </span>
          </p>
          <LaisLastUpdate
            v-if="lastUpdatedLabel"
            :value="lastUpdatedLabel"
            align="left"
          />
        </div>
      </div>

      <div
        class="min-h-0 flex-1 overflow-auto px-4 pb-[calc(5rem+env(safe-area-inset-bottom))] sm:px-6 sm:pb-4 lg:px-10 max-md:flex-none max-md:overflow-visible"
      >
        <div class="grid gap-4 pb-4">
          <AgentActivityAlert
            :show="showAgentActivityAlert"
            @action="openAgentActivity"
          />
          <QualifiedVisitorAlert :show="hasQualifiedVisitor" />
          <section
            v-if="showScheduledClientsSection"
            class="grid gap-3 rounded-lais border border-lais-border bg-lais-bg-soft p-4"
          >
            <div
              class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <h2 class="text-[14px] font-semibold text-lais-text">
                {{ SCHEDULED_VISITS_TEXT.PAGE_TITLE }}
              </h2>
              <VisitsOrderByDropdown
                v-if="scheduledClientCards.length"
                :model-value="scheduledClientsOrder"
                @update:model-value="selectScheduledClientsOrder"
              />
            </div>
            <div
              v-if="isLoading && !scheduledClientCards.length"
              class="grid gap-3"
            >
              <div
                v-for="index in 2"
                :key="index"
                class="h-24 animate-pulse rounded-lais border border-lais-border bg-white"
              />
            </div>
            <div
              v-else-if="loadError"
              class="rounded-lais border border-rose-100 bg-white px-4 py-8 text-center text-[13px] font-semibold text-rose-700"
            >
              {{ SCHEDULED_VISITS_TEXT.ERROR_TITLE }}
            </div>
            <div
              v-else-if="!scheduledClientCards.length"
              class="rounded-lais border border-dashed border-lais-border bg-white px-4 py-8 text-center text-[13px] font-semibold text-lais-text-body"
            >
              {{ SCHEDULED_VISITS_TEXT.EMPTY_TITLE }}
            </div>
            <div v-else class="grid gap-3 lg:grid-cols-2">
              <ScheduledClientCard
                v-for="client in scheduledClientCards"
                :key="client.id"
                :client="client"
                @expand="onScheduledClientToggle(client, $event)"
                @profile="openVisitorInfo(client.visit)"
                @message="openVisitMessage(client.visit)"
              />
            </div>
          </section>
        </div>

        <VisitNowAnchor v-if="visitas.length" />
        <div class="grid gap-3 md:hidden">
          <template v-for="group in groupedVisitsByDay" :key="group.label">
            <VisitSectionTitle :label="group.label" />
            <div
              v-for="visita in group.visits"
              :key="visita.id"
              class="grid gap-2"
            >
              <div class="flex items-start gap-3">
                <input
                  type="checkbox"
                  :checked="selected.has(visita.id)"
                  class="mt-4 size-4 accent-lais-primary rounded"
                  @change="toggleOne(visita.id)"
                />
                <div class="min-w-0 flex-1">
                  <DoneVisitRichCard
                    v-if="isRichDoneVisit(visita)"
                    :visit="visitCardView(visita)"
                    :unsuccessful="
                      cardStatus(visita) !== VISITA_CARD_STATUS.DONE
                    "
                    :offer-label="doneOfferActionLabel(visita)"
                    @message-resident="openVisitMessage(visita)"
                    @message-owner="openQuickActions(visita)"
                    @offer="sendOfferForVisit(visita)"
                    @call="callVisitContact(visita)"
                  />
                  <StalledVisitCard
                    v-else-if="isStalledVisit(visita)"
                    :visit="visitCardView(visita)"
                  />
                  <PendingFinishVisitCard
                    v-else-if="shouldShowPendingFinish(visita)"
                    :visit="visitCardView(visita)"
                    :disabled="!canFinishVisitNow(visita)"
                    @finish="openFinishVisitModal(visita)"
                  />
                  <VisitStatusCard
                    v-else
                    :visit="visitCardView(visita)"
                    :status-label="statusLabel(visita)"
                    :status-variant="cardStatusVariant(visita)"
                    :show-confirm="shouldShowConfirmCTA(visita)"
                    :show-reschedule="shouldShowRescheduleCTA(visita)"
                    :show-finish="shouldShowFinishCTA(visita)"
                    :show-offer="shouldShowOfferCTA(visita)"
                    :show-cancel="canCancelVisitAction(visita)"
                    :offer-label="offerActionLabel(visita)"
                    :blocked="isVisitBlocked(visita)"
                    :checked-in="hasVisitorCheckIn(visita)"
                    :is-pool="isPoolVisit(visita)"
                    @confirm="openVisitConfirmation(visita)"
                    @reschedule="rescheduleVisit(visita)"
                    @cancel="openCardCancel(visita)"
                    @cancel-group="openCardCancel(visita)"
                    @finish="openFinishVisitModal(visita)"
                    @offer="sendOfferForVisit(visita)"
                    @actions="openQuickActions(visita)"
                  />
                  <p
                    v-if="visitRuntimeBadge(visita)"
                    class="mt-2 inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold"
                    :class="runtimeToneClass(visitRuntimeBadge(visita).tone)"
                    :title="visitRuntimeBadge(visita).title"
                  >
                    <span
                      class="size-3.5"
                      :class="visitRuntimeBadge(visita).icon"
                      aria-hidden="true"
                    />
                    <span class="truncate">{{
                      visitRuntimeBadge(visita).label
                    }}</span>
                  </p>
                </div>
              </div>
              <QualifiedVisitorAlert :show="isQualifiedVisitor(visita)" />
            </div>
          </template>

          <VisitsListState
            v-if="visitas.length === 0"
            :loading="isLoading"
            :error="loadError"
            :loading-state="visitsLoadingState"
            :empty-state="visitsEmptyState"
            @retry="fetchVisitas"
          />
        </div>

        <VisitsOperationalTable
          :visitas="visitas"
          :selected="selected"
          :all-selected="allSelected"
          :is-loading="isLoading"
          :load-error="loadError"
          :visits-loading-state="visitsLoadingState"
          :visits-empty-state="visitsEmptyState"
          :visit-table-labels="visitTableLabels"
          :visit-copy="operationalTableCopy"
          :per-page="perPage"
          :page="page"
          :total-pages="totalPages"
          :can-manage-availability="canManageAvailability"
          :can-review-visits="canReviewVisits"
          :can-review-visits-action="canReviewVisitsAction"
          :status-class="statusClass"
          :status-label="statusLabel"
          :is-visit-blocked="isVisitBlocked"
          :visiting-blocked-reason="visitingBlockedReason"
          :cancellation-reason="cancellationReason"
          :is-visit-expired="isVisitExpired"
          :visit-time-expired-label="VISIT_TIME_EXPIRED_LABEL"
          :visit-runtime-badge="visitRuntimeBadge"
          :runtime-tone-class="runtimeToneClass"
          :listing-context="listingContext"
          :visit-intention="visitIntention"
          :entry-authorization="entryAuthorization"
          :has-visitor-check-in="hasVisitorCheckIn"
          :format-date="formatDate"
          :format-time="formatTime"
          :visit-card-view="visitCardView"
          :should-show-confirm-c-t-a="shouldShowConfirmCTA"
          :can-cancel-visit-action="canCancelVisitAction"
          :can-reschedule-visit-action="canRescheduleVisitAction"
          :is-pool-visit="isPoolVisit"
          :can-follow-up-visit-action="canFollowUpVisitAction"
          @retry="fetchVisitas"
          @toggle-all="toggleAll"
          @toggle-one="toggleOne"
          @open-details="abrirDetalheVisita"
          @open-blocked-sheet="openBlockedSheet"
          @open-confirmation="openVisitConfirmation"
          @open-cancel="openCardCancel"
          @reschedule="rescheduleVisit"
          @open-quick-actions="openQuickActions"
          @open-finish="openFinishVisitModal"
          @review="revisarVisita"
          @change-per-page="changePerPage"
          @previous-page="goToPreviousPage"
          @next-page="goToNextPage"
        />
      </div>

      <LaisActionBar :actions="actions">
        <template #trailing>
          <button
            type="button"
            class="text-[13px] font-semibold text-lais-primary underline decoration-lais-gold decoration-[1.5px] underline-offset-[3px] hover:decoration-lais-primary"
            @click="abrirComoFunciona"
          >
            Como funciona a visita na Imudei?
          </button>
        </template>
      </LaisActionBar>
    </div>

    <div
      v-else-if="isAgendaView && !hasOperationalPanelSection"
      class="flex min-h-0 flex-1 flex-col max-md:flex-none max-md:overflow-visible"
    >
      <div class="shrink-0 px-4 pt-4 sm:px-6 lg:px-10">
        <LaisMetricStrip
          :items="agendaDayMetrics"
          :loading="isLoading && activeAgendaVisits.length === 0"
          aria-label="Resumo das visitas do dia"
        />
      </div>
      <VisitsAgendaTimeline
        :visits="activeAgendaVisits"
        :is-loading="isLoading"
        :load-error="loadError"
        :is-current-date="isActiveAgendaDateToday"
        :last-updated-label="lastUpdatedAgendaLabel"
        :day-label="activeAgendaDayLabel"
        :show-agent-activity-alert="showAgentActivityAlert"
        :has-qualified-visitor="hasQualifiedVisitor"
        :error-title="VISITS_ERROR_STATE_TITLE"
        :error-description="VISITS_ERROR_STATE_DESCRIPTION"
        @retry="fetchAgendaVisits"
        @agent-activity="openAgentActivity"
      >
        <template #visit="{ visita }">
          <div class="grid min-w-0 gap-2">
            <DoneVisitRichCard
              v-if="isRichDoneVisit(visita)"
              :visit="visitCardView(visita)"
              :unsuccessful="cardStatus(visita) !== VISITA_CARD_STATUS.DONE"
              :offer-label="doneOfferActionLabel(visita)"
              @message-resident="openVisitMessage(visita)"
              @message-owner="openQuickActions(visita)"
              @offer="sendOfferForVisit(visita)"
              @call="callVisitContact(visita)"
            />
            <StalledVisitCard
              v-else-if="isStalledVisit(visita)"
              :visit="visitCardView(visita)"
            />
            <PendingFinishVisitCard
              v-else-if="shouldShowPendingFinish(visita)"
              :visit="visitCardView(visita)"
              :disabled="!canFinishVisitNow(visita)"
              @finish="openFinishVisitModal(visita)"
            />
            <VisitStatusCard
              v-else
              :visit="visitCardView(visita)"
              :status-label="statusLabel(visita)"
              :status-variant="cardStatusVariant(visita)"
              :show-confirm="shouldShowConfirmCTA(visita)"
              :show-reschedule="shouldShowRescheduleCTA(visita)"
              :show-finish="shouldShowFinishCTA(visita)"
              :show-offer="shouldShowOfferCTA(visita)"
              :show-cancel="canCancelVisitAction(visita)"
              :offer-label="offerActionLabel(visita)"
              :blocked="isVisitBlocked(visita)"
              :checked-in="hasVisitorCheckIn(visita)"
              :is-pool="isPoolVisit(visita)"
              @confirm="openVisitConfirmation(visita)"
              @reschedule="rescheduleVisit(visita)"
              @cancel="openCardCancel(visita)"
              @cancel-group="openCardCancel(visita)"
              @finish="openFinishVisitModal(visita)"
              @offer="sendOfferForVisit(visita)"
              @actions="openQuickActions(visita)"
            />
            <p
              v-if="visitRuntimeBadge(visita)"
              class="inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold"
              :class="runtimeToneClass(visitRuntimeBadge(visita).tone)"
              :title="visitRuntimeBadge(visita).title"
            >
              <span
                class="size-3.5"
                :class="visitRuntimeBadge(visita).icon"
                aria-hidden="true"
              />
              <span class="truncate">{{
                visitRuntimeBadge(visita).label
              }}</span>
            </p>
            <QualifiedVisitorAlert :show="isQualifiedVisitor(visita)" />
          </div>
        </template>
      </VisitsAgendaTimeline>
    </div>

    <VisitasOperationalPanel v-else :view="operationalView" />

    <button
      v-if="isAgendaView && canCreateVisit"
      type="button"
      class="z-30 mb-4 mr-4 inline-flex h-11 shrink-0 items-center gap-2 self-end rounded-lais bg-lais-primary px-4 text-[14px] font-semibold text-white shadow-lais-card transition-colors hover:bg-lais-primary-700 sm:fixed sm:bottom-5 sm:right-5 sm:mb-0 sm:mr-0"
      aria-label="Nova visita"
      :disabled="!canCreateVisitAction"
      @click="novaVisita"
    >
      <span class="i-lucide-plus size-4" aria-hidden="true" />
      Nova visita
    </button>

    <UnreadVisitMessageSnackbar
      :show="Boolean(unreadMessageVisit)"
      :message="
        unreadMessageVisit
          ? `${unreadCountForVisit(unreadMessageVisit)} mensagem(ns) não lida(s) nesta visita`
          : ''
      "
      @action="openUnreadMessageVisit(unreadMessageVisit)"
    />

    <KeyHolderInfoDialog
      :show="showKeyHolderInfoDialog"
      :link="keyHolderLink"
      @close="showKeyHolderInfoDialog = false"
    />

    <VisitConfirmationModal
      :show="showVisitConfirmationModal"
      :success="showVisitConfirmationSuccess"
      :visit="visitCardView(confirmationVisit)"
      :loading="visitConfirmationLoading"
      @close="closeVisitConfirmation"
      @action="finishVisitConfirmation"
      @confirm="confirmVisitFromPanel"
      @edit-entry="openKeyHolderInfoDialog(confirmationVisit)"
    />

    <VisitBlockedSheetModal
      :show="showBlockedSheet"
      :visit="blockedVisit"
      :reason="visitingBlockedReason(blockedVisit)"
      :is-loading="blockedNotifyLoading"
      :is-done="blockedNotifyDone"
      :error-message="blockedNotifyError"
      @close="closeBlockedSheet"
      @notify="notifyBlockedAvailability"
      @similar="goToBlockedSimilar"
    />

    <VisitQuickActionsDrawer
      :show="showQuickActionsDrawer"
      :active-visit="activeQuickVisit"
      :quick-visit-address="quickVisitAddress"
      :quick-visit-date-text="quickVisitDateText"
      :is-blocked="isVisitBlocked(activeQuickVisit)"
      :blocked-reason="visitingBlockedReason(activeQuickVisit)"
      :has-visitor-check-in="hasVisitorCheckIn(activeQuickVisit)"
      :can-send-introduction="
        canUseQuickVisitMessages && canSendIntroduction(activeQuickVisit)
      "
      :can-send-offer="canSendVisitOffer && canSendOffer(activeQuickVisit)"
      :can-suggest-house="
        canUseQuickVisitMessages && canSuggestHouse(activeQuickVisit)
      "
      :can-confirm="activeQuickActionMatrix.confirm.allowed"
      :can-cancel="activeQuickActionMatrix.cancel.allowed"
      :can-reschedule="activeQuickActionMatrix.reschedule.allowed"
      :can-follow-up="activeQuickActionMatrix.followUp.allowed"
      :can-add-more="activeQuickActionMatrix.addMore.allowed"
      :introduction-label="
        quickActionCompletedLabel(AGENDA_QUICK_ACTIONS.INTRODUCTION)
      "
      :offer-label="quickActionCompletedLabel(AGENDA_QUICK_ACTIONS.OFFER)"
      :suggest-house-label="
        quickActionCompletedLabel(AGENDA_QUICK_ACTIONS.SUGGEST_HOUSE)
      "
      @close="closeQuickActions"
      @open-blocked="openBlockedSheet(activeQuickVisit)"
      @open-visitor-info="openVisitorInfo(activeQuickVisit)"
      @send-introduction="sendQuickIntroduction"
      @send-offer="sendQuickOffer"
      @start-support="startQuickSupport"
      @suggest-house="suggestQuickHouse"
      @reschedule="rescheduleQuickVisit"
      @want-cancel="openWantToCancel"
      @confirm="openVisitConfirmation(activeQuickVisit)"
      @talk-owner="talkToOwner"
      @follow-up="followUpQuickVisit"
      @add-more="addMoreVisits"
      @open-agenda-info="openAgendaInfo"
      @open-key-holder="openKeyHolderInfoDialog(activeQuickVisit)"
    />

    <VisitWantCancelModal
      :show="showWantToCancelModal"
      :active-visit="activeQuickVisit"
      :quick-visit-date-text="quickVisitDateText"
      :has-visitor-check-in="hasVisitorCheckIn(activeQuickVisit)"
      @close="showWantToCancelModal = false"
      @reschedule="rescheduleQuickVisit"
      @continue="continueQuickCancellation"
    />

    <VisitAgendaInfoModal
      :show="showAgendaInfoModal"
      :title="agendaInfoTitle"
      :kind="agendaInfoKind"
      :can-update-entry="!!propertyId(activeQuickVisit)"
      @close="showAgendaInfoModal = false"
      @update-entry="goToQuickVisitEntryEdit"
      @add-more="addMoreVisits"
    />

    <VisitCreateVisitModal
      :show="showCreateVisitModal"
      @close="showCreateVisitModal = false"
      @start="startVisitCreation"
    />

    <VisitFinishChoiceModal
      :show="showFinishVisitModal"
      :candidate="finishVisitCandidate"
      :image-src="finishVisitImageSrc"
      :address="finishVisitAddress"
      :date-text="finishVisitDateText"
      @close="showFinishVisitModal = false"
      @finish="finalizarVisita"
    />

    <LaisModal
      v-if="isOperationalList"
      :show="showCancelModal"
      title="Cancelar visita"
      max-width-class="max-w-[560px]"
      @close="showCancelModal = false"
    >
      <div class="grid gap-4">
        <div
          class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] leading-relaxed text-lais-text-body"
        >
          <h3 class="text-[15px] font-semibold text-lais-text">
            {{ VISIT_CANCEL_CONFIRM_TITLE }}
          </h3>
          <p class="mt-1 text-lais-text-helper">
            {{ VISIT_CANCELLATION_NOTICE }}
          </p>
          <p class="mt-2 text-[12px] font-semibold text-lais-text-muted">
            {{ selected.size }}
            {{
              selected.size === 1
                ? 'visita selecionada'
                : 'visitas selecionadas'
            }}.
          </p>
        </div>

        <div
          v-if="selectedVisit"
          class="rounded-lais-sm border border-lais-border bg-white px-4 py-3 text-[13px] text-lais-text-body"
        >
          <h3 class="text-[14px] font-semibold text-lais-text">
            {{ VISIT_CANCEL_DETAILS_TITLE }}
          </h3>
          <dl class="mt-3 grid gap-2">
            <div class="grid gap-0.5">
              <dt class="font-semibold text-lais-text">Endereço:</dt>
              <dd>{{ selectedVisitAddress || '—' }}</dd>
            </div>
            <div class="grid gap-0.5">
              <dt class="font-semibold text-lais-text">Cliente:</dt>
              <dd class="flex min-w-0 items-center gap-1">
                <span class="truncate">{{
                  selectedVisit.contact?.name || '—'
                }}</span>
                <span
                  v-if="hasVisitorCheckIn(selectedVisit)"
                  class="i-lucide-circle-check size-4 shrink-0 text-emerald-700"
                  title="Check-in confirmado"
                  aria-label="Check-in confirmado"
                />
              </dd>
            </div>
            <div class="grid gap-0.5">
              <dt class="font-semibold text-lais-text">Data:</dt>
              <dd>{{ selectedVisitDateText }}</dd>
            </div>
          </dl>
        </div>

        <div
          v-if="selectedVisit"
          class="flex flex-col gap-2 rounded-lais-sm border border-lais-border bg-white px-4 py-3 text-[13px] text-lais-text-body sm:flex-row sm:items-center sm:justify-between"
        >
          <span>{{ VISIT_CANCEL_RESCHEDULE_TEXT }}</span>
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-full border border-lais-border px-3 py-2 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary hover:bg-lais-primary-50"
            @click="editarVisita(selectedVisit.id)"
          >
            <span class="i-lucide-calendar-clock size-3.5" aria-hidden="true" />
            {{ VISIT_CANCEL_RESCHEDULE_LINK }}
          </button>
        </div>

        <div
          v-if="selectedVisit"
          class="grid gap-3 rounded-lais-sm border border-lais-primary/30 bg-lais-primary-50 px-4 py-3 text-[13px] text-lais-text-body"
        >
          <div class="flex items-start gap-3">
            <span
              class="i-lucide-calendar-clock mt-0.5 size-5 shrink-0 text-lais-primary"
              aria-hidden="true"
            />
            <div class="min-w-0 flex-1">
              <h3 class="text-[14px] font-semibold text-lais-text">
                Aproveite e revise os horários para visitas
              </h3>
              <p class="mt-1 leading-relaxed text-lais-text-helper">
                Evite novos cancelamentos. Selecione abaixo os horários nos
                quais o imóvel pode receber visitas. Isso irá refletir no site
                da Imudei.
              </p>
            </div>
          </div>
          <div class="flex justify-end">
            <button
              v-if="canManageAvailability"
              type="button"
              class="inline-flex items-center gap-2 rounded-full border border-lais-primary bg-white px-3 py-2 text-[12px] font-semibold text-lais-primary transition-colors hover:bg-lais-primary hover:text-white"
              @click="openAvailabilityModal(selectedVisit)"
            >
              <span class="i-lucide-clock-3 size-3.5" aria-hidden="true" />
              Horários para visita
            </button>
          </div>
        </div>

        <section class="grid gap-3">
          <div>
            <h3 class="text-[14px] font-semibold text-lais-text">
              {{ VISIT_CANCEL_REASON_TITLE }}
            </h3>
            <p class="mt-1 text-[12px] text-lais-text-muted">
              Escolha a pessoa ou parte que não consegue seguir com a visita.
            </p>
          </div>
          <div class="grid gap-2 sm:grid-cols-2">
            <button
              v-for="option in VISIT_CANCEL_RESPONSIBLE_OPTIONS"
              :key="option.value"
              type="button"
              class="min-h-[58px] rounded-lais-sm border px-3 py-2 text-left text-[13px] font-semibold transition-colors"
              :class="
                cancelForm.responsible === option.value
                  ? 'border-lais-primary bg-lais-primary-50 text-lais-primary'
                  : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
              "
              :aria-pressed="cancelForm.responsible === option.value"
              @click="
                cancelForm.responsible = option.value;
                updateCancelResponsible();
              "
            >
              {{ option.label }}
            </button>
          </div>
          <p
            v-if="cancelReasonHelperMessage"
            class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-3 py-2 text-[13px] text-lais-text-body"
          >
            {{ cancelReasonHelperMessage }}
          </p>
        </section>

        <section class="grid gap-3">
          <div>
            <h3 class="text-[14px] font-semibold text-lais-text">
              Qual o motivo?
            </h3>
            <p class="mt-1 text-[12px] text-lais-text-muted">
              Esse motivo ficará registrado no histórico da visita.
            </p>
          </div>
          <div class="grid max-h-[260px] gap-2 overflow-y-auto pr-1">
            <button
              v-for="option in cancelReasonOptions"
              :key="option.value"
              type="button"
              class="rounded-lais-sm border px-3 py-2 text-left text-[13px] leading-relaxed transition-colors"
              :class="
                cancelForm.reason === option.value
                  ? 'border-lais-primary bg-lais-primary-50 font-semibold text-lais-primary'
                  : 'border-lais-border bg-white text-lais-text-body hover:border-lais-primary hover:text-lais-primary'
              "
              :aria-pressed="cancelForm.reason === option.value"
              @click="cancelForm.reason = option.value"
            >
              {{ option.label }}
            </button>
          </div>
        </section>

        <label class="grid gap-1">
          <span
            class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
          >
            Observação
          </span>
          <textarea
            v-model="cancelForm.notes"
            rows="3"
            class="w-full rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text placeholder:text-lais-text-muted focus:border-lais-primary focus:outline-none"
            placeholder="Detalhe o contexto quando necessário"
          />
        </label>

        <label
          v-if="canSuspendListing"
          class="flex items-start gap-3 rounded-lais border border-lais-border bg-white px-3 py-3 text-[13px] text-lais-text"
        >
          <input
            v-model="cancelForm.suspendListing"
            type="checkbox"
            class="mt-0.5 size-4 accent-lais-primary"
          />
          <span>
            <strong class="block">{{ VISIT_CANCEL_SUSPEND_TITLE }}</strong>
            <span class="mt-2 block font-semibold text-lais-primary">
              {{ VISIT_CANCEL_SUSPEND_LABEL }}
            </span>
            <span class="mt-1 block text-lais-text-muted">
              {{ VISIT_CANCEL_SUSPEND_HELPER }}
            </span>
          </span>
        </label>

        <div
          v-if="cancelForm.suspendListing"
          class="grid gap-3 rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3 text-[13px] text-lais-text-body"
        >
          <div class="flex items-start gap-3">
            <span
              class="i-lucide-pause-circle mt-0.5 size-5 shrink-0 text-lais-primary"
              aria-hidden="true"
            />
            <div class="min-w-0">
              <h3 class="text-[14px] font-semibold text-lais-text">
                {{ VISIT_CANCEL_SUSPEND_FLOW_TITLE }}
              </h3>
              <p class="mt-1 leading-relaxed">
                {{ VISIT_CANCEL_SUSPEND_FLOW_BODY }}
              </p>
              <p class="mt-2 leading-relaxed text-lais-text-helper">
                {{ VISIT_CANCEL_SUSPEND_BODY }}
              </p>
            </div>
          </div>
          <p class="font-semibold text-lais-text">
            {{ VISIT_CANCEL_SUSPEND_PERIOD_LABEL }}
          </p>
          <div class="grid gap-3 sm:grid-cols-2">
            <label class="grid gap-1">
              <span
                class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
              >
                De
              </span>
              <input
                v-model="cancelForm.suspensionStartsAt"
                type="date"
                class="h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
              />
            </label>
            <label class="grid gap-1">
              <span
                class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
              >
                {{ VISIT_CANCEL_SUSPEND_LAST_DAY_LABEL }}
              </span>
              <input
                v-model="cancelForm.suspensionEndsAt"
                type="date"
                class="h-10 w-full rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text focus:border-lais-primary focus:outline-none"
              />
            </label>
          </div>
          <p
            v-if="suspensionAlert"
            class="rounded-lais-sm border border-lais-gold/40 bg-lais-gold/10 px-3 py-2 font-semibold text-lais-text"
          >
            {{ suspensionAlert }}
          </p>
        </div>
      </div>

      <template #footer>
        <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <LaisPrimaryButton variant="ghost" @click="showCancelModal = false">
            Voltar
          </LaisPrimaryButton>
          <LaisPrimaryButton
            variant="primary"
            icon="i-lucide-ban"
            :disabled="!cancelForm.reason || !canCancelVisits"
            @click="cancelarSelecionadas"
          >
            {{ cancelConfirmLabel }}
          </LaisPrimaryButton>
        </div>
      </template>
    </LaisModal>

    <VisitAvailabilityModal
      v-if="isOperationalList"
      :show="showAvailabilityModal"
      :availability-visit="availabilityVisit"
      :availability-days="availabilityDays"
      :active-availability-day="activeAvailabilityDay"
      :selected-availability-day="selectedAvailabilityDay"
      :is-loading="isLoadingAvailability"
      :is-saving="isSavingAvailability"
      :error-message="availabilityError"
      :show-anticipation-unavailable="showAnticipationUnavailable"
      :anticipation-unavailable-reason="anticipationUnavailableReason"
      :loading-label="t('VISITS.COMMON.LOADING')"
      :error-title="t('VISITS.ERRORS.LOAD_TITLE')"
      :retry-label="t('VISITS.COMMON.RETRY')"
      :high-demand-label="VISIT_HIGH_DEMAND_LABEL"
      :availability-date-id="availabilityDateId"
      :availability-label="availabilityLabel"
      :slot-time-id="slotTimeId"
      :slot-high-demand="slotHighDemand"
      :availability-slot-disabled="availabilitySlotDisabled"
      :availability-slot-status="availabilitySlotStatus"
      @close="showAvailabilityModal = false"
      @go-listing="goToAvailabilityListing"
      @keep-current="keepCurrentVisitAfterAnticipation"
      @retry="loadAvailabilityOptions"
      @select-day="selectedAvailabilityDay = $event"
      @toggle-slot="toggleAvailabilitySlot"
      @warning-selected="trackAvailabilityWarning"
      @save="saveAvailabilityOptions"
    />
  </LaisGamificationSurface>
</template>
